import React, {useEffect, useState, useRef, startTransition} from "react";
import {Helmet} from "react-helmet";
import {useNavigate} from "react-router-dom";

import "../App.css";
import "../CSS/programmes/ItemCreator.css";

import {ColorModeIcon} from "../context/ColorModeContext.tsx";
import Navbar from "../components/Navbar.tsx";
import CategoryTree from "../components/CategoryTree";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CustomTextField from "../components/CustomTextField.tsx";
import {useCurrentApp} from "../context/CurrentAppContext";
import axios from "axios";
import CustomCheckbox from "../components/CustomCheckbox.tsx";
import {DefaultButton, SubmitButton} from "../components/Buttons.tsx";
import CustomSelect from "../components/CustomSelect.tsx";
import SearchableMultiSelect from "../components/SearchableMultiSelect.tsx";
import SearchableSelect from "../components/SearchableSelect.tsx";
import AutocompleteNumberTextInput from "../components/AutocompleteNumberTextInput.tsx";
import AutocompleteTextInput from "../components/AutocompleteTextInput.tsx";
import AutocompleteTextInputTree from "../components/AutocompleteTextInputTree.tsx";
import {HooverTooltip, EmptyTooltipPlaceholder} from "../components/InfoField.tsx";
import {Typography, Stack, StackProps} from '@mui/material';
import DragAndDrop from "../components/DragAndDrop.tsx"
import {DndContext, closestCenter, DragEndEvent} from "@dnd-kit/core";
import {SortableContext, arrayMove} from "@dnd-kit/sortable";

// Interface for the Produktdetails tree items
interface TreeItemNode {
  id: string;
  label: string;
  children?: TreeItemNode[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;
  if (value !== index) return null;

  return (
    <div
      role="tabpanel"
      id={`tab-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{
        width: '100%',
        minHeight: '300px', // Only active panel gets this height
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'start',
      }}
      {...other}
    >
      {children}
    </div>
  );
}

const renderErrorMessage = (errorMessage) => {
  if (errorMessage) {
    return (
      <div className="error-message-container">
        {" "}
        <p>{errorMessage}</p>{" "}
      </div>
    );
  } else {
    return null;
  }
};

const renderInfoMessage = (infoMessage) => {
  if (infoMessage) {
    return (
      <div className="info-message-container" style={{width: "100%", minWidth: "100%"}}>
        {" "}
        <p>{infoMessage}</p>{" "}
      </div>
    );
  } else {
    return null;
  }
};

const tabStyle = {
  '&.Mui-selected': {
    color: 'var(--naturGreen)',
  }
};

// Methods for checking whether the selected categories are actually the deepest possible categories
/**
 * Returns only the "leaf" categories from selectedCategories.
 * A leaf is one that doesn't have any child also in selectedCategories.
 */
function getLeafCategories(_selectedCategories) {
  return _selectedCategories.filter(cat => {
    // If there's another category that starts with cat + '.', then cat has a child
    return !_selectedCategories.some(other => other !== cat && other.startsWith(cat + "."));
  });
}

function isDeepestCategory(cat, categoryPaths) {
  // Returns false if there is a path that starts with cat + "."
  // meaning cat has deeper subcategories
  return !categoryPaths.some(path => path.startsWith(cat + "."));
}

/**
 * Anything regarding tische or betten categories should have a stock quantity of 5.
 * Everything else has a stock quantity of 10.
 **/
function getStockQuantity(selectedCategories: Map<number, string>, allCategories: {
  id: number,
  name: string,
  path: string
}[]) {
  const basePaths = [
    "Wohnzimmer.Polstermöbel.Sofa_Couch",
    "Wohnzimmer.Polstermöbel.Funktionssofas",
    "Wohnzimmer.Polstermöbel.Garnituren",
    "Schlafzimmer.Schlafsofas",
    "Schlafzimmer.Betten", // Alles aus Betten
    "Schlafzimmer.Tische",
    "Schlafzimmer.Kleiderschränke", // Alles aus Kleiderschränke
    "Schlafzimmer.Kommoden.Vertiko_Kombischränke",
    "Schlafzimmer.Kommoden.Wäschekommoden",
    "Baby_Kinderzimmer.Babyzimmer", // Alles aus Babyzimmer
    "Baby_Kinderzimmer.Komplettsets", // Alles aus Komplettsets
    "Baby_Kinderzimmer.Kinder_Jugendzimmer.Betten_Hochbetten",
    "Baby_Kinderzimmer.Kinder_Jugendzimmer.Hausbetten",
    "Baby_Kinderzimmer.Kinder_Jugendzimmer.Sofas_Schlafsofas",
    "Wohnzimmer.Tische", // Alles aus Tische
    "Esszimmer.Tische", // Alles aus Tische
    "Esszimmer.Komplettsets", // Alles aus Komplettsets
    "Baby_Kinderzimmer.Kinder_Jugendzimmer.Regale_Kommoden",
    "Baby_Kinderzimmer.Kinder_Jugendzimmer.Kleiderschränke",
    "Baby_Kinderzimmer.Kinder_Jugendzimmer.Schreibtische",
    "Büro.Konferenztische",
    "Büro.Schreibtische",
    "Büro.Stehpulte",
    "Büro.Regale_Bibliothek",
    "Büro.Sekretäre",
    "Büro.Schränke",
    "Garderobe.Schränke",
    "Gartenmöbel.Tische",
    "Loungemöbel.Tische",
    "Tische.Esstische",
    "Tische.Beistelltische",
    "Tische.Tischgestelle",
    "Tische.Tischplatten",
  ];
  const fiveStockIds = new Set<number>(
    allCategories
      .filter(cat => // First filter all the categories and subcategories based on the basePaths
        basePaths.some(prefix => cat.path.toLowerCase().startsWith(prefix.toLowerCase())
        ))
      .map(cat => cat.id) // Then extract the category ids
  );
  return Array.from(selectedCategories.keys()).some(id => fiveStockIds.has(id)) ? 5 : 10;
}

/** To sanitize values from the backend. If 0 or 0.0 replace it by an empty string and if it is a number, remove .0 at the end **/
const sanitizeValue = (val) => {
  if (val === "0" || val === "0.0") return "";
  return typeof val === "string" ? val.replace(/\.0$/, '') : val;
};

function ItemCreator() {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  const navigate = useNavigate();

  // Current app, which is used to update the current app in the Navbar, when navigating to another page
  const {setCurrentApp} = useCurrentApp();


  // ---------- ItemCreatorStartPage ----------
  const [isItemCreatorVisible, setIsItemCreatorVisible] = useState(false); // TODO: Set to false after testing is done
  const [isCopyArticle, setIsCopyArticle] = useState<boolean>(false);
  const [copyItemID, setCopyItemID] = useState<string>("");
  const [copyItemPreviewDescription, setCopyItemPreviewDescription] = useState<string>("");
  const [selectedShops, setSelectedShops] = useState<{value: number, label: string}[]>([{ value: 10159, label: "Natur24" }]); // 10159 = Natur24, 73461 = Gartengarten24
  const [varNr, setVarNr] = useState<string>("");
  const [ean, setEan] = useState<string>("");
  const [isLengthUsed, setIsLengthUsed] = useState<boolean>(false);
  const [initialCreationInfoMessage, setInitialCreationInfoMessage] = useState<string | null>(null);
  const [initialCreationErrorMessage, setInitialCreationErrorMessage] = useState<string | null>(null);
  const [startPageErrors, setStartPageErrors] = useState<{
    varNr?: string;
    ean?: string;
    categories?: string;
    copyItemID?: string;
  }>({});

  // ---------- ItemCreatorPage ----------
  const [selectedTab, setSelectedTab] = useState<number>(0); // TODO: Set to 0 after testing is done
  const [lastIncompleteTab, setLastIncompleteTab] = useState(-1);
  // Variables for checking if everything is done or has been set already in case of POST requests
  const [completedRequests, setCompletedRequests] =
    useState<{
      put_stammdaten: boolean;
      set_availability_to_variation_markets: boolean;
      set_variationsupplier: boolean;
      set_stock: boolean;
      post_ean1: boolean;
      post_ean2: boolean;
      set_prices: boolean;
      set_categories: boolean;
      set_variation_properties: boolean;
      set_availability_masse_mainwarehouse_ek_price: boolean;
      set_texts: boolean;
      set_images: boolean;
    }>({
      put_stammdaten: false,
      set_availability_to_variation_markets: false,
      set_variationsupplier: false,
      set_stock: false,
      post_ean1: false,
      post_ean2: false,
      set_prices: false,
      set_categories: false,
      set_variation_properties: false,
      set_availability_masse_mainwarehouse_ek_price: false,
      set_texts: false,
      set_images: false
    });
  const [alreadyProcessedPostRequests, setAlreadyProcessedPostRequests] =
    useState<{
      set_availability_to_variation_markets: boolean;
      set_variationsupplier: boolean;
      set_stock: boolean;
      post_ean1: boolean;
      post_ean2: boolean;
      set_variation_properties: boolean;
      set_texts: boolean;
      set_images: boolean;
    }>({
      set_availability_to_variation_markets: false,
      set_variationsupplier: false,
      set_stock: false,
      post_ean1: false,
      post_ean2: false,
      set_variation_properties: false,
      set_texts: false,
      set_images: false
    });
  // Regarding TabView Disabling shows which backend requests are in each page and used to check if all necessary requests of one page have been processed correctly
  const tabRequestsMapping = {
    0: ['put_stammdaten', 'set_availability_to_variation_markets', 'set_variationsupplier', 'set_stock', 'post_ean1', 'post_ean2'],
    1: ['set_prices'],
    2: ['set_categories'],
    3: ['set_variation_properties'],
    4: ['set_availability_masse_mainwarehouse_ek_price', 'set_texts'],
    5: ['set_images'],
    6: []
  };
  const getFirstIncompleteTab = () => {
    let earliestIncomplete = Infinity;
    Object.keys(tabRequestsMapping).forEach((tabKey) => {
      const tabIndex = Number(tabKey);
      const hasIncomplete = tabRequestsMapping[tabIndex].some(
        (requestKey) => !completedRequests[requestKey]
      );
      if (hasIncomplete && tabIndex < earliestIncomplete) {
        earliestIncomplete = tabIndex;
      }
    });
    // If no incomplete tab is found, return -1
    return earliestIncomplete === Infinity ? -1 : earliestIncomplete;
  };
  useEffect(() => {
    setLastIncompleteTab(getFirstIncompleteTab());
  }, [completedRequests]);

  // Get the previously created item
  const _prev = sessionStorage.getItem("prevItem");
  const prevItem = _prev ? JSON.parse(_prev) : null;

  // States for mandatory information
  const [itemID, setItemID] = useState<string>("");
  const [varID, setVarID] = useState<string>("");
  const [infoMessage, setinfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // --- Variables ---
  // Stammdaten
  const [manufacturers, setManufacturers] = useState<{
    value: string | number;
    label: string;
    manufacturerId: number;
    manufacturerName: string;
    supplierId: number;
    supplierName: string
  }[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<{
    value: string | number;
    label: string;
    manufacturerId: number;
    manufacturerName: string;
    supplierId: number;
    supplierName: string
  }>(null);
  const [selectedManufacturerKind, setSelectedManufacturerKind] = useState<{
    value: string | number;
    label: string
  }>(null); // Hiermit findet man flag2, mainWarehouseId heraus
  const [isLagerarticle, setIslagerArticle] = useState<boolean>(false);
  const [selectedMainWarehouseId, setSelectedMainWarehouseId] = useState<{
    value: string | number;
    label: string
  }>(null);
  const [mainWarehouseStock, setMainWarehouseStock] = useState<string>("");
  const [selectedAvailability, setSelectedAvailability] = useState<number | string>("");
  const [isVarNrChanged, setIsVarNrChanged] = useState<boolean>(false);
  // EAN can be found in ItemCreatorStartPage
  const [isEanChanged, setIsEanChanged] = useState<boolean>(false);
  const [isEan2Changed, setIsEan2Changed] = useState<boolean>(false);
  const [ean2, setEan2] = useState<string>("");
  const [useMarketsAvailabilities, setUseMarketsAvailabilities] = useState<boolean>(false);
  const [isDisableUseMarketsAvailabilities, setIsDisableUseMarketsAvailabilities] = useState<boolean>(false); // Used to disable the checkbox of marketsavailabilities and the multiselect if a manufacturer with a constraint is used
  const [marketsAvailabilities, setMarketsAvailabilities] = useState<{ value: string | number; label: string }[]>([]);
  const [selectedMarketsAvailabilities, setSelectedMarketsAvailabilities] = useState<{
    value: string | number;
    label: string
  }[]>([]);
  const [manufacturerMarketsAvailabilityConstraints, setManufacturerMarketsAvailabilityConstraints] = useState<{
    [manufacturerId: string]: { label: string; value: string | number; }[]
  }>({});
  const [hasInitializedMarketsSelect, setHasInitializedMarketsSelect] = useState(false);
  const [stammdatenErrors, setStammdatenErrors] = useState<{
    selectedManufacturer?: string;
    selectedManufacturerKind?: string;
    selectedMainWarehouseId?: string;
    mainWarehouseStock?: string;
    selectedAvailability?: string;
    varNr?: string;
    ean?: string;
    ean2?: string;
    marketsAvailabilities?: string;
  }>({});
  const [stammdatenInfoMessage, setStammdatenInfoMessage] = useState<string>("");
  const [stammdatenErrorMessage, setStammdatenErrorMessage] = useState<string>("")
  // Prices
  const [priceEK, setPriceEK] = useState<string>("");
  const [priceLaden, setPriceLaden] = useState<string>("");
  const [priceWebshop, setPriceWebshop] = useState<string>("");
  const [priceVerkaufsportNational, setPriceVerkaufsportNational] = useState<string>("");
  const [priceOTTO, setPriceOTTO] = useState<string>("");
  const [priceVerkaufsportInternational, setPriceVerkaufsportInternational] = useState<string>("");
  const [priceEBayAT, setPriceEBayAT] = useState<string>("");
  const [priceUVP, setPriceUVP] = useState<string>("");
  const [preisangabenErrors, setPreisangabenErrors] = useState<{
    EK?: string;
    laden?: string;
    webshop?: string;
    verkaufsportalNational?: string;
    OTTO?: string;
    verkaufsportalInternational?: string;
    eBayAT?: string;
    UVP?: string;
  }>({});
  const [preisangabenErrorMessage, setPreisangabenErrorMessage] = useState<string>("");
  // Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategoriesNatur24, setSelectedCategoriesNatur24] = useState<Map<number, string>>(new Map());
  const [selectedCategoriesGG24, setSelectedCategoriesGG24] = useState<Map<number, string>>(new Map());
  const selectedCategories = new Map([...selectedCategoriesNatur24, ...selectedCategoriesGG24]);
  const [ottoCats, setOttoCats] = useState<{
    [plentyCatId: number]: { plenty_cat_name: string, otto_cat_id: number, otto_cat_name: string }
  } | null>(null);
  const [selectedOttoCat, setSelectedOttoCat] = useState<{
    ottoCatId: number,
    ottoCatName: string,
    plentyCatId: number,
    plentyCatName: string
  } | null>(null);
  const [ebayCats, setEbayCats] = useState<{
    [plentyCatId: number]: { plenty_cat_name: string, ebay_category_id_1: number, ebay_category_id_2: number }
  } | null>(null);
  const [selectedEbayCat, setSelectedEbayCat] = useState<{
    ebayCatId1: number,
    EbayCatId2: number,
    plentyCatId: number,
    plentyCatName: string
  } | null>(null);
  const [hoodCats, setHoodCats] = useState<{
    [plentyCatId: number]: { plenty_cat_name: string, hood_category_id: number }
  } | null>(null);
  const [selectedHoodCat, setSelectedHoodCat] = useState<{
    hoodCatId: number,
    plentyCatId: number,
    plentyCatName: string
  } | null>(null);
  const [categoryPanelError, setCategoryPanelError] = useState<string | null>(null);
  // Handle Category selections and Otto, Ebay and Hood additions and deletions
  useEffect(() => {
    // Handle Deletion: Check if
    // Get current plenty IDs from selectedCategories
    const currentPlentyIds = new Set(Array.from(selectedCategories).map(([id]) => id));
    // Reset category states if their plentyCatId no longer exists in selectedCategories
    let otto = selectedOttoCat && currentPlentyIds.has(selectedOttoCat.plentyCatId)
      ? selectedOttoCat
      : null;
    let ebay = selectedEbayCat && currentPlentyIds.has(selectedEbayCat.plentyCatId)
      ? selectedEbayCat
      : null;
    let hood = selectedHoodCat && currentPlentyIds.has(selectedHoodCat.plentyCatId)
      ? selectedHoodCat
      : null;

    // Handle Addition
    // Sort selectedCategories by descending depth (e.g. Wohnzimmer.Leuchten.Leuchten before Wohnzimmer.Leuchten)
    const sortedCategories = Array.from(selectedCategories)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .sort(([_, pathA], [__, pathB]) => pathB.split('.').length - pathA.split('.').length);
    // Loop through sorted categories to fill in missing category selections
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [plenty_id, _path] of sortedCategories) {
      if (!otto && ottoCats[plenty_id] !== undefined) {
        const ottoCat = ottoCats[plenty_id];
        otto = {
          ottoCatId: ottoCat.otto_cat_id,
          ottoCatName: ottoCat.otto_cat_name,
          plentyCatId: plenty_id,
          plentyCatName: ottoCat.plenty_cat_name
        };
        console.log(plenty_id + " has an otto category: " + JSON.stringify(ottoCat));
      }
      if (!ebay && ebayCats[plenty_id] !== undefined) {
        const ebayCat = ebayCats[plenty_id];
        ebay = {
          ebayCatId1: ebayCat.ebay_category_id_1,
          EbayCatId2: ebayCat.ebay_category_id_2,
          plentyCatId: plenty_id,
          plentyCatName: ebayCat.plenty_cat_name
        };
        console.log(plenty_id + " has an ebay category: " + JSON.stringify(ebayCat));
      }
      if (!hood && hoodCats[plenty_id] !== undefined) {
        const hoodCat = hoodCats[plenty_id];
        hood = {
          hoodCatId: hoodCat.hood_category_id,
          plentyCatId: plenty_id,
          plentyCatName: hoodCat.plenty_cat_name
        };
        console.log(plenty_id + " has a hood category: " + JSON.stringify(hoodCat));
      }
      // If all categories are set, no need to continue looping
      if (otto && ebay && hood) break;
    }
    // Update state only if there's a change
    if (otto !== selectedOttoCat) { // This should also prompt a resending of the set_variation_properties request!
      setSelectedOttoCat(otto);
      setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
    }
    if (ebay !== selectedEbayCat) setSelectedEbayCat(ebay);
    if (hood !== selectedHoodCat) setSelectedHoodCat(hood);
    const currUseLiegeFlaeche = useLiegeFlaeche;
    const newUseLiegeFlaeche = (liegeFlaecheIds.some(id => currentPlentyIds.has(id)));
    if (currUseLiegeFlaeche != newUseLiegeFlaeche) {
      setUseLiegeFlaeche(newUseLiegeFlaeche);
      setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
    }
    const currUseHaerteGrad = useHaerteGrad;
    const newUseHaerteGrad = (haerteGradIds.some(id => currentPlentyIds.has(id)));
    if (currUseHaerteGrad != newUseHaerteGrad) {
      setUseHaerteGrad(newUseHaerteGrad);
      setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories]);
  // Maße
  const [lengthOrDepth, setLengthOrDepth] = useState<string>("");
  const [width, setWidth] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [woodTypes, setWoodTypes] = useState<{ value: string | number; label: string }[]>([]);
  const [selectedWoodTypes, setSelectedWoodTypes] = useState<{ value: string | number; label: string }[]>([]);
  const [materials, setMaterials] = useState<{ value: string | number; label: string }[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<{ value: string | number; label: string }[]>([]);
  const [colors, setColors] = useState<{ value: string | number; label: string }[]>([]);
  const [selectedColors, setSelectedColors] = useState<{ value: string | number; label: string }[]>([]);
  const liegeFlaecheIds = [57 /* Betten */, 405 /* Lattenrost */, 75 /* Matratzen */, 386 /* Himmelbetten */, 61 /* Boxspringbetten */, 330 /* Balkenbetten */, 269 /* Metallfreie Betten */, 59 /* 80-100 cm */, 58 /* 120-160 cm */, 60 /* 180-200 cm */]
  const [useLiegeFlaeche, setUseLiegeFlaeche] = useState<boolean>(false);
  const liegeFlaechen = [{value: 74, label: "90x200"}, {value: 75, label: "100x200"}, {
    value: 76,
    label: "140x200"
  }, {value: 77, label: "160x200"}, {value: 78, label: "180x200"}, {value: 79, label: "200x200"}, {
    value: 80,
    label: "Sonderlänge über 200 cm"
  }, {value: 81, label: "Sonderbreite über 200 cm"}, {value: 84, label: "120x200"}, {
    value: 85,
    label: "Sonderlänge unter 200 cm"
  }, {value: 86, label: "170x200"}, {value: 89, label: "80x200"}];
  const [selectedLiegeFlaeche, setSelectedLiegeFlaeche] = useState<{ value: string | number; label: string }>(null);
  const haerteGradIds = [75 /* Matratzen */]
  const [useHaerteGrad, setUseHaerteGrad] = useState<boolean>(false);
  const haerteGrade = [{value: 168, label: "H1"}, {value: 169, label: "H1/H2"}, {
    value: 170,
    label: "H2"
  }, {
    value: 171,
    label: "H2/H3"
  }, {value: 172, label: "H3"}, {value: 173, label: "H3/H4"}, {
    value: 174,
    label: "H4"
  }];
  const [selectedHaerteGrad, setSelectedHaerteGrad] = useState<{ value: string | number; label: string }>(null);
  const [masseErrors, setMasseErrors] = useState<{
    lengthOrDepth?: string;
    width?: string;
    height?: string;
    weight?: string;
    selectedWoodTypesOrMaterials?: string;
    selectedColors?: string;
    selectedLiegeFlaeche?: string;
    selectedHaerteGrad?: string;
  }>({});
  const [masseErrorMessage, setMasseErrorMessage] = useState<string>("");
  // Produkttexte
  const [name1And2, setName1And2] = useState<string>("");
  const [name3, setName3] = useState<string>("");
  const [previewDescription, setPreviewDescription] = useState<string>("");
  const [ueberschrift1, setUeberschrift1] = useState<string>("");
  const defaultLieferumfangSuggestions = ["(ohne Dekoration und weiter gezeigte Möbel)", "(ohne Dekoration, Kissen und weiter gezeigte Möbel)", "(ohne Dekoration, Matratzen und weiter gezeigte Möbel)", "(ohne Dekoration, Beleuchtung und weiter gezeigte Möbel)", "(ohne Roste, Matratzen, Nachtkonsolen, Dekoration und weiter gezeigte Möbel)", "(ohne Roste, Matratzen, Bett, Dekoration und weiter gezeigte Möbel)",]
  const [lieferumfangSuggestions, setLieferumfangSuggestions] = useState<string []>(defaultLieferumfangSuggestions);
  const [lieferumfang, setLieferumfang] = useState<{ quantity: number, text: string }[]>([]);
  const defaultProduktDatenSuggestions = ["Liegefläche: L x B cm", "Sitzbreite: ca. B cm", "Sitzhöhe: ca. H cm", "Sitztiefe: ca. T cm", "Einlegetiefe ca. T cm", "Holzstärke: ca. ZAHL cm", "Stärke der Holzbeine: ca. B x B cm", "Höhe bis Oberkante Bettrahmen: ca. H cm", "Höhe bis Unterkante Bettrahmen: ca. H cm", "Gesamthöhe mit Kopfteil: ca. H cm"];
  const [produktdatenSuggestions, setProduktdatenSuggestions] = useState<string []>(defaultProduktDatenSuggestions);
  const [produktdaten, setProduktdaten] = useState<string []>([]);
  const produktdetailsSuggestions = ["Lieferzustand: Zerlegt", "Lieferzustand: Teilmontiert", "Lieferzustand: Montiert", "Anwendbarkeit: indoor", "Anwendbarkeit: outdoor", "Zeitloser Stil", "Moderner Stil", "Natürlicher Stil", "Natürlicher moderner Stil", "Industrieller Stil", "Schicker Stil", "Klassischer Stil", "Landhausstil", "Zeitlos", "Komplett aus massiver geölter Eiche", "Massives Eichenholz", "Altes Eichenholz", "Recyceltes Eichenholz", "Aus MDF in Eichenoptik", "Komplett aus massiver geölter Buche", "Massives Buchenholz", "Altes Buchenholz", "Recyceltes Buchenholz", "Aus MDF in Buchenoptik", "Komplett aus massiver geölter Kiefer", "Massives Kiefernholz", "Altes Kiefernholz", "Recyceltes Kiefernholz", "Aus MDF in Kiefernoptik", "Komplett aus massiver geölter Kernbuche", "Massive Kernbuche", "Alte Kernbuche", "Recycelte Kernbuche", "Aus MDF in Kernbuchenoptik", "Komplett aus massivem geöltem Teakholz", "Massives Teakholz", "Altes Teakholz", "Recyceltes Teakholz", "Aus MDF in Teakholzoptik", "Komplett aus massivem geöltem Mangoholz", "Massives Mangoholz", "Altes Mangoholz", "Recyceltes Mangoholz", "Aus MDF in Mangoholzoptik", "Pflegeleichtes und langlebiges Material", "Einfache Montage", "Griffe aus Metall", "Griffe aus Aluminium", "Griffe aus Holz", "Durchgehende Lamellen", "Schweizer Kante", "Fischgrätenmuster", "Mit wunderschönen Holzmaserungen", "Armlehne links und rechts", "Vertikal und horizontal montierbar", "Stahlgestell", "Sonderlänge ZAHL cm", "Max. Gewicht:", "Max. Belastbarkeit: ZAHL kg", "Stuhlbeinhöhe: ca. ZAHL cm", "Stabile massive Holzbeine", "Schneller Auszugsmechanismus"];
  const defaultProduktdetails = [{id: "0", label: "Zustand: Neu und originalverpackt", children: []}, {
    id: "1",
    label: "Besondere Merkmale:",
    children: []
  }];
  const [produktdetails, setProduktdetails] = useState<TreeItemNode[]>(defaultProduktdetails); // Ids of default items need to be 0 and 1
  const [ueberschrift2, setUeberschrift2] = useState<string>("");
  const [articleText, setArticleText] = useState<string>("");
  const isIgnoreInvalidWords = false; // const [isIgnoreInvalidWords, setIsIgnoreInvalidWords] = useState<boolean>(false); // feature to ignore invalid words was removed
  const [htmlPreview, setHtmlPreview] = useState<string>("");
  const [invalidWords, setInvalidWords] = useState<string []>([]);
  const [produktTexteErrors, setProduktTexteErrors] = useState<{
    name1And2?: string;
    name3?: string;
    previewDescription?: string;
    ueberschrift1?: string;
    lieferumfang?: string;
    produktdaten?: string;
    produktdetails?: string;
    ueberschrift2?: string;
    articleText?: string;
    produktTexteErrors?: string;
  }>({});
  const [produktTexteInfoMessage, setProduktTexteInfoMessage] = useState<string>("");
  const [produktTexteErrorMessage, setProduktTexteErrorMessage] = useState<string>("");
  const name1And2Ref = useRef(null);
  const name3Ref = useRef(null);
  const previewDescriptionRef = useRef(null);
  const ueberschrift1Ref = useRef(null);
  const lieferumfangRef = useRef(null);
  const produktdatenRef = useRef(null);
  const produktdetailsRef = useRef(null);
  const ueberschrift2Ref = useRef(null);
  const articleTextRef = useRef(null);
  const produktTexteErrorsRef = useRef(null);
  // Produktbilder
  const [images, setImages] = useState(Array(12).fill(null)); // 12 empty slots
  const [imagesInfoMessage, setImagesInfoMessage] = useState<string>("");
  const [errorMessageImageSize, setErrorMessageImageSize] = useState("");
  //used in Produktbilder panel for checking image sizes when images are uploaded, deleted or image positions are changed
  useEffect(() => {
    if (!Array.isArray(images) || images.length === 0) return;

    // keeping track of mounting state can prevent some errors when eg reloading the page while the function runs
    let isMounted = true;

    (async () => {  //IIFE to handle async logic
      const smallImages = [];

      const imagePromises = images
        .filter(Boolean)
        .map((base64Image, index) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.src = base64Image;

            img.onload = () => {
              if (img.naturalWidth < 500 || img.naturalHeight < 500) {
                smallImages.push(index + 1);
              }
              resolve();
            };

            img.onerror = () => resolve(); // Prevents Promise rejection
          })
        );

      await Promise.all(imagePromises);

      if (isMounted) {
        if (smallImages.length === 1) {
          setErrorMessageImageSize(`Achtung: Bild ${smallImages[0]} ist zu klein und muss eventuell später hochskaliert werden.`);
        } else if (smallImages.length > 1) {
          setErrorMessageImageSize(`Achtung: Bilder ${smallImages.join(", ")} sind zu klein und müssen eventuell später hochskaliert werden.`);
        } else {
          setErrorMessageImageSize("");
        }
      }
    })(); //IIFE runs immediately (when hook is activated)

    return () => {
      isMounted = false;
    };
  }, [images]);


  // --- Metrics tracking ---
  const statisticsRetryCount = useRef(0);
  const [creationStartTime, setCreationStartTime] = useState<number | null>(null);
  const [tabStartTime, setTabStartTime] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [tabDurations, setTabDurations] = useState<{ [key: number]: number }>({0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0});

  useEffect(() => {
    if (isItemCreatorVisible) {
      const now = Date.now();
      setCreationStartTime(now);
      setActiveTab(selectedTab);
      setTabStartTime(now);
      console.log("Item creation started at:", new Date(now).toISOString());
    }
  }, [isItemCreatorVisible]);

  useEffect(() => {
    if (tabStartTime === null || activeTab === null) return;

    const now = Date.now();

    // Update duration of the previous tab
    setTabDurations(prev => ({
      ...prev,
      [activeTab]: ((prev[activeTab] || 0) + (now - tabStartTime) / 1000) // convert to seconds
    }));

    // Start timing the new tab
    setActiveTab(selectedTab);
    setTabStartTime(now);
  }, [selectedTab]);

  useEffect(() => {
    const allComplete = Object.values(completedRequests).every(v => v === true);
    if (allComplete && selectedTab === 6 && creationStartTime) {
      const _date = Date.now()
      const _totalTime = (_date - creationStartTime) / 1000;
      const _deepestCategoryID = getDeepestCategoryID();
      const _deepestCategoryName = selectedCategories.get(_deepestCategoryID);
      const _startTime = new Date(creationStartTime).toISOString();
      const _endTime = new Date(_date).toISOString();
      const statistics_data = {
        ArticleID: itemID,
        startTime: _startTime,
        endTime: _endTime,
        totalTime: _totalTime,
        tabDurations: tabDurations,
        isCopyArticle: isCopyArticle,
        supplierID: selectedManufacturer.supplierId,
        supplierName: selectedManufacturer.supplierName,
        deepestCategoryID: _deepestCategoryID,
        deepestCategoryName: _deepestCategoryName
      };

      // Try sending the statistics
      axios.post(`${backendUrl}/item_creator/post_statistics/`, statistics_data, config)
        .then(() => {
          alert("Deine Daten wurden hochgeladen");
          statisticsRetryCount.current = 0;   // reset on success
        })
        .catch(() => {
          statisticsRetryCount.current += 1;

          if (statisticsRetryCount.current < 2) {
            // Retry once more
            axios.post(`${backendUrl}/item_creator/post_statistics/`, statistics_data, config)
              .then(() => {
                statisticsRetryCount.current = 0;
              })
              .catch(() => {
                alert("Fehler beim Hochladen der Daten. Bitte informiere Jonathan.");
              });
          } else {
            alert("Fehler beim Hochladen der Statistik-Daten. Bitte informiere Jonathan.");
          }
        });

      // console.log("✅ All requests completed. Total creation time:", _totalTime.toFixed(2), "seconds");
      // console.log("⏱️ Time spent on each tab (seconds):", Object.fromEntries(
      //   Object.entries(tabDurations).map(([k, v]) => [k, +v.toFixed(2)])
      // ));
    }
  }, [completedRequests, selectedTab]);


  // Item erstellen

  // Get needed Information from the backend like general categories, Otto, Ebay and Hood categories
  useEffect(() => {
    const fetchData = async () => {
      await axios.get(`${backendUrl}/item_creator/get_item_creator_information/`, config)
        .then(response => {
          setCategories(response.data.generalCategories);
          setOttoCats(response.data.ottoCategories);
          setEbayCats(response.data.ebayCategories);
          setHoodCats(response.data.hoodCategories);
          const formattedManufacturer = response.data.manufacturers.map((item) => ({
            ...item,
            label: item.manufacturerName as string,
            value: Number(item.manufacturerId)
          }));
          setManufacturers(formattedManufacturer);
          const formattedWoodTypes = Object.entries(response.data.woodTypes).map(([value, label]) => ({
            label: label as string,
            value: Number(value)
          }));
          setWoodTypes(formattedWoodTypes);
          const formattedMaterials = Object.entries(response.data.materials).map(([value, label]) => ({
            label: label as string,
            value: Number(value)
          }));
          setMaterials(formattedMaterials);
          const formattedColors = Object.entries(response.data.colors).map(([value, label]) => ({
            label: label as string,
            value: Number(value)
          }));
          setColors(formattedColors);
          setInvalidWords(response.data.invalidWords);
          const formattedMarketsAvailabilities = Object.entries(response.data.marketsAvailabilities).map(([label, value]) => ({
            label: label,
            value: value as string | number
          }));
          setMarketsAvailabilities(formattedMarketsAvailabilities);
          setManufacturerMarketsAvailabilityConstraints(response.data.manufacturerMarketsAvailabilityConstraints)
        })
        .catch(err => {
          const err_text = err.response?.data?.detail?.toString() || err.toString();
          alert("Fehler beim Laden benötigter Informationen vom Backend für den ItemCreator. Versuche die Seite neuzuladen! \n" + err_text);
        });
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedShops.some(s => s.value === 10159)) setSelectedCategoriesNatur24(new Map());
    if (!selectedShops.some(s => s.value === 73461)) setSelectedCategoriesGG24(new Map());
  }, [selectedShops]);

  // Track variable states for testing purposes
  // useEffect(() => {console.log();}, []);
  useEffect(() => {
    console.log(selectedMarketsAvailabilities);
  }, [selectedMarketsAvailabilities]);
  useEffect(() => {
    console.log(completedRequests);
  }, [completedRequests]);// Layout for the MUI Stack element used to display info tooltips next to the textfields
  const stackProps: StackProps = {
    direction: 'row',
    spacing: 1,
    alignItems: 'center',
    justifyContent: 'center',
    sx: {
      width: '100%',
    },
  };

  const getDeepestCategoryID = () => {
    let maxDepth = -1;
    let deepestCategoryID = null;
    selectedCategories.forEach((path, id) => {
      const depth = path.split('.').length;
      if (depth > maxDepth) {
        maxDepth = depth;
        deepestCategoryID = id;
      }
    });
    return deepestCategoryID;
  }

  const handleInitialCreationClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // Checken ob alles gesetzt ist

    // Check whether the selected categories are actually the deepest categories
    const categoryPaths = categories.map(category => category.path);
    const selectedCategoriesPaths = Array.from(selectedCategories.values());
    const deepestSelectedCategoryPaths = getLeafCategories(selectedCategoriesPaths);
    const hasNonDeepestSelected = deepestSelectedCategoryPaths.some(cat => !isDeepestCategory(cat, categoryPaths));

    const newErrors = {
      varNr: !varNr ? "Artikel-Nr. ist erforderlich." : null,
      ean: !ean
        ? "Gebe eine korrekte EAN an."
        : ean.length < 12
          ? "Die EAN muss mindestens 12 Zeichen lang sein."
          : ean.length > 13
            ? "Die EAN darf maximal 13 Zeichen lang sein."
            : null,
      copyItemID: isCopyArticle && !copyItemID ? "Artikel ID ist erforderlich, wenn 'Artikel kopieren' aktiviert ist." : null,
      categories:
        (selectedShops.some(s => s.value === 10159) && selectedCategoriesNatur24.size === 0) ? "Bitte wähle eine Kategorie für Natur24 aus." :
        (selectedShops.some(s => s.value === 73461) && selectedCategoriesGG24.size === 0) ? "Bitte wähle eine Kategorie für Gartengarten24 aus." :
        hasNonDeepestSelected ? "Bitte wähle immer die tiefstmögliche Kategorie/ Unterkategorie aus!" : null
    };

    // Set all errors in one state update
    setStartPageErrors(newErrors);

    // If any error exists, prevent submission
    if (Object.values(newErrors).some((error) => error)) return;

    // Plenty Anfrage
    setinfoMessage(null);
    setErrorMessage(null);
    const data = {
      is_copy_article: isCopyArticle,
      copy_item_id: copyItemID,
      var_nr: varNr,
      ean: ean,
      initial_categories: Array.from(selectedCategories.keys()),
      deepestInitialCategoryID: getDeepestCategoryID(),
      mandants: selectedShops.map(s => s.value)
    };

    await axios
      .post(
        `${backendUrl}/item_creator/create_initial_item/`,
        data,
        config
      )
      .then((res) => {
        const data = res.data
        setItemID(data.itemId)
        setVarID(data.varId)
        setIsLengthUsed(data.isLengthUsed);
        const copy = data.copiedItem;
        if (copy) {
          setSelectedAvailability(copy.selectedAvailability);
          setPriceEK(copy.priceEK);
          setPriceLaden(copy.priceLaden);
          setPriceWebshop(copy.priceWebshop);
          setPriceVerkaufsportNational(copy.priceVerkaufsportNational);
          setPriceOTTO(copy.priceOTTO);
          setPriceVerkaufsportInternational(copy.priceVerkaufsportInternational);
          setPriceEBayAT(copy.priceEBayAT);
          setPriceUVP(copy.priceUVP);
          setLengthOrDepth(sanitizeValue(copy.lengthOrDepth));
          setWidth(sanitizeValue(copy.width));
          setHeight(sanitizeValue(copy.height));
          setWeight(sanitizeValue(copy.weight));
          setName1And2(copy.name1And2);
          setName3(copy.name3);
          const copyManufacturer = copy.selectedManufacturer;
          if (copy.selectedManufacturer) {
            console.log(copy.selectedManufacturer);
            const newManufacturer = {
              ...copyManufacturer,
              value: copyManufacturer?.manufacturerId,
              label: copyManufacturer?.manufacturerName
            };
            console.log(newManufacturer)
            const newHasConstraint = newManufacturer.value in manufacturerMarketsAvailabilityConstraints;
            console.log(newHasConstraint);
            if (newHasConstraint) { // New one has a constraint -> select the corresponding markets availabilities
              setUseMarketsAvailabilities(true);
              setIsDisableUseMarketsAvailabilities(true);
              setSelectedMarketsAvailabilities(manufacturerMarketsAvailabilityConstraints[newManufacturer.value]);
              setCompletedRequests(prev => ({
                ...prev,
                put_stammdaten: false,
                set_variationsupplier: false,
                set_availability_to_variation_markets: false
              }));
            }
            setSelectedManufacturer(newManufacturer);
          } else {
            alert("Der Hersteller konnte nicht übernommen werden!")
          }
          setSelectedManufacturerKind(copy.selectedManufacturerKind);
          setUeberschrift1(copy.ueberschrift1);
          setLieferumfang(copy.lieferumfang);
          setProduktdaten(copy.produktdaten);
          setProduktdetails(copy.produktdetails);
          setUeberschrift2(copy.ueberschrift2);
          setArticleText(copy.articleText);
          setSelectedWoodTypes(copy.selectedWoodTypes);
          setSelectedMaterials(copy.selectedMaterials);
          setSelectedColors(copy.selectedColors);
          setCopyItemPreviewDescription(copy.previewDescription);
        }
        // Only lock markets if Gartengarten24 is the ONLY shop selected
        const isGartengartenOnly = selectedShops.length === 1 && selectedShops[0].value === 73461;
        if (isGartengartenOnly) {
          setUseMarketsAvailabilities(true);
          setIsDisableUseMarketsAvailabilities(true);
          // Force exactly these two markets: WebShop (1.00) and WEB-API (148.00)
          setSelectedMarketsAvailabilities(marketsAvailabilities.filter(m => m.value === '1.00' || m.value === '148.00'));
        }

        setInitialCreationInfoMessage(`Erstellen des Artikels mit ID: ${data.itemId} und Varianten ID: ${data.varId} war erfolgreich. ${isCopyArticle && data.copiedItem ? "\n" + `Der Artikel mit ID ${copyItemID} wurde erfolgreich kopiert.` : ""}`);
        setInitialCreationErrorMessage(data.problems + `${isCopyArticle && !data.copiedItem ? "\n" + `Der Artikel mit ID ${copyItemID} wurde nicht erfolgreich kopiert.` : ""}`)
        // Save this item as a cookie
        sessionStorage.setItem("prevItem", JSON.stringify({itemID: data.itemId, varID: data.varId}));
        // Handle successful response
        setIsItemCreatorVisible(true);
        // Now the Categories are completely set and cannot be changed anymore. But there might be an error in setting the higher categories
        // So if that happens, we need to run them again in the categories tab
        const areHigherCategoriesSet = data.AreHigherCategoriesSet;
        setCompletedRequests(prev => ({...prev, set_categories: areHigherCategoriesSet}));
      })
      .catch((err) => {
        const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
          : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : err.response.data?.detail?.toString();
        setInitialCreationErrorMessage(error_str);
        alert(error_str);
      });


  };

  const handleEanChange = (e: React.ChangeEvent<HTMLInputElement>, setEanMethod, setPageErrors, fieldName) => {
    const newValue = e.target.value;
    let errorMessage = null;
    if (newValue.length > 13) {
      errorMessage = "Die EAN darf maximal 13 Zeichen lang sein.";
    } else if (newValue.length < 12 && newValue.length > 0) {
      errorMessage = "Die EAN muss mindestens 12 Zeichen lang sein.";
    }
    setPageErrors((prevErrors) => ({...prevErrors, [fieldName]: errorMessage}));
    setEanMethod(newValue);
  };

  const RenderInitialItemCreatorPage = () => {
    // Texts for the information tooltips:
    const info_art_nr = "Hier wird die Artikelnummer des Herstellers eingetragen. Diese kann aus Zahlen, Buchstaben und Zeichen bestehen."
    const info_EAN = "Die EAN ist 12-13-stellig. Ist eine EAN vom Hersteller/Lieferant vorgegeben, wird diese verwendet. Gibt es vom Hersteller/Lieferant keine EAN, wird auf den Natu24-EAN-Kreis zurückgegriffen.";
    const info_categories = "Bitte wähle eine tiefstmögliche Kategorie aus. Hierbei musst du immer die tiefstmögliche Kategorie auswählen, also eine Kategorie ohne Unterkategorien, (Also ohne den Pfeil links neben dem Kästchen)! Du kannst das Suchfeld ebenfalls nutzen, um deine Kategorie zu finden.";

    return (<div className="ItemCreatorStartPage">
      <p>Es können vereinzelt Anzeigefehler auftreten, also bitte nochmal Korrekturlesen</p>
      <div
        style={{
          marginTop: "15px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          width: "100%"
        }}>
        <CustomCheckbox label={"Artikel kopieren"} value={isCopyArticle} onChange={setIsCopyArticle}
                        is_disabled={false} single_line_label={true}/>
        <CustomTextField label={"Artikel-ID"}
                         value={copyItemID} onChange={(e) => {
          const newValue = e.target.value;
          setStartPageErrors((prevErrors) => ({
            ...prevErrors,
            copyItemID: newValue ? null : prevErrors.copyItemID
          }));
          setCopyItemID(e.target.value);
        }}
                         inputMode={"integer"} is_required={isCopyArticle} is_disabled={!isCopyArticle}
                         manualError={!!startPageErrors.copyItemID}
                         errorText={startPageErrors.copyItemID || ""}
        />
        {prevItem && <p style={{color: "var(--primary-colorSwitched)"}}>Letzter Artikel: {prevItem.itemID}</p>}
      </div>
      <Stack{...stackProps}>
        <CustomTextField label={"Artikel-Nr."}
                         value={varNr} onChange={(e) => {
          const newValue = e.target.value;
          setStartPageErrors((prevErrors) => ({...prevErrors, varNr: newValue ? null : prevErrors.varNr}));
          setVarNr(newValue);
        }}
                         inputMode={"text"} is_required={true}
                         manualError={!!startPageErrors.varNr}
                         errorText={startPageErrors.varNr || ""}
        />
        <HooverTooltip content={info_art_nr}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"EAN"}
                         value={ean}
                         onChange={(e) => handleEanChange(e, setEan, setStartPageErrors, "ean")}
                         inputMode={"integer"}
                         is_required={true}
                         manualError={!!startPageErrors.ean}
                         errorText={startPageErrors.ean || ""}
        />
        <HooverTooltip content={info_EAN}/>
      </Stack>
      <div style={{display: "flex", justifyContent: "center", width: "100%", marginTop: "15px", gap: "20px"}}>
        <p style={{margin: 0, alignSelf: "center"}}>Mandant(en) wählen:</p>
        <SearchableMultiSelect
          inputLabel="Shop(s)"
          items={[
            { value: 10159, label: "Natur24" },
            { value: 73461, label: "Gartengarten24" }
          ]}
          value={selectedShops}
          setValue={(val) => setSelectedShops(val)}
          selectAllOption={true}
        />
      </div>
      <p>Wähle eine Kategorie, in welche dieser Artikel bei der Erstellung einzuordnen ist.</p>

      {/* Natur24 Tree */}
      {selectedShops.some(s => s.value === 10159) && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", marginBottom: "20px" }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
            <p style={{ margin: 0, fontWeight: "bold" }}>Bitte wähle die Kategorie für den Shop Natur24 aus:</p>
            <HooverTooltip content={info_categories} />
          </div>
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <CategoryTree
              selectedItems={selectedCategoriesNatur24}
              onSelectedItemsChange={(newInitialSelectedCategories) => {
                setSelectedCategoriesNatur24(newInitialSelectedCategories);
                setStartPageErrors((prevErrors) => ({
                  ...prevErrors,
                  categories: (newInitialSelectedCategories.size === 0) ? "Wähle mindestens eine Kategorie aus." : null
                }));
              }}
              singleSelection={false}
              isMultiSelect={false}
              preloadedCategories={categories.filter(c => c.shop_id === 10159)}
            />
          </div>
        </div>
      )}

      {/* Gartengarten24 Tree */}
      {selectedShops.some(s => s.value === 73461) && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", marginBottom: "20px" }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
            <p style={{ margin: 0, fontWeight: "bold" }}>Bitte wähle die Kategorie für den Shop Gartengarten24 aus:</p>
            <HooverTooltip content={info_categories} />
          </div>
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <CategoryTree
              selectedItems={selectedCategoriesGG24}
              onSelectedItemsChange={(newInitialSelectedCategories) => {
                setSelectedCategoriesGG24(newInitialSelectedCategories);
                setStartPageErrors((prevErrors) => ({
                  ...prevErrors,
                  categories: (newInitialSelectedCategories.size === 0) ? "Wähle mindestens eine Kategorie aus." : null
                }));
              }}
              singleSelection={false}
              isMultiSelect={false}
              preloadedCategories={categories.filter(c => c.shop_id === 73461)}
            />
          </div>
        </div>
      )}
      <div
        style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: '0'}}>
        <CustomCheckbox
          label={"Otto Kategorie"}
          value={!!selectedOttoCat} // Null is falsy -> ! null is true -> !! null is false so it is only checked if an Otto Category is selected
          onChange={() => {
          }}
          is_disabled={true}/>
        <CustomCheckbox
          label={"Ebay Kategorie"}
          value={!!selectedEbayCat}
          onChange={() => {
          }}
          is_disabled={true}/>
        <CustomCheckbox
          label={"Hood Kategorie"}
          value={!!selectedHoodCat}
          onChange={() => {
          }}
          is_disabled={true}/>
      </div>
      {renderErrorMessage(startPageErrors.categories)}
      {!selectedEbayCat && (
        // full-width wrapper so the paragraph doesn't force sibling sizing
        <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
          <p style={{
            color: "yellow",
            maxWidth: "600px",       // limit line length
            textAlign: "center",
            margin: "0 16px"         // small breathing room
          }}>
            Vorsicht: Wenn die von dir gewählte Kategorie keine Ebay-Verknüpfung besitzt,
            könnte es ggf. Probleme beim Erstellen eines Listings geben.
          </p>
        </div>
      )}
      <SubmitButton label={"Erstellen"} onClick={handleInitialCreationClick}/>
      {renderErrorMessage(initialCreationErrorMessage)}
    </div>);
  };

  // ItemCreator tabs

  const checkStammdaten = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setStammdatenErrorMessage("");
    // Checken ob alles gesetzt ist
    const newErrors = {
      selectedManufacturer: !selectedManufacturer ? "Wähle einen Hersteller aus." : null,
      selectedManufacturerKind: !selectedManufacturerKind ? "Wähle eine Herstellerart aus." : null,
      selectedMainWarehouseId: (isLagerarticle && !selectedMainWarehouseId) ? "Wähle ein Lager aus." : null,
      mainWarehouseStock: (isLagerarticle && !mainWarehouseStock) ? "Gebe eine Zahl an." : null,
      selectedAvailability: !selectedAvailability ? "Wähle eine Verfügbarkeit aus." : null,
      varNr: !varNr ? "Gebe die Artikelnr. an." : null,
      ean: !ean
        ? "Gebe eine korrekte EAN an."
        : ean.length < 12
          ? "Die EAN muss mindestens 12 Zeichen lang sein."
          : ean.length > 13
            ? "Die EAN darf maximal 13 Zeichen lang sein."
            : null,
      ean2: ean2 && ean2.length < 12
        ? "Die EAN 2 muss mindestens 12 Zeichen lang sein."
        : ean2 && ean2.length > 13
          ? "Die EAN 2 darf maximal 13 Zeichen lang sein."
          : null,
      marketsAvailabilities: (useMarketsAvailabilities && selectedMarketsAvailabilities.length == 0) ? "Wähle mindestens eine Marktverfügbarkeit aus!" : null
    };
    // Set all errors in one state update
    if (Object.values(newErrors).some((error) => error)) {
      setStammdatenErrors(newErrors);
      return;
    }

    // Check ean validity
    if (isEanChanged) {
      setStammdatenInfoMessage("Überprüfe geänderte EAN");
      try {
        await axios.post(`${backendUrl}/item_creator/check_ean_validity/`, {ean: ean}, config);
        setIsEanChanged(false);
      } catch (err) {
        setStammdatenInfoMessage("")
        let error_str: string;
        if (!err.response) {
          error_str = "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar.";
        } else if (err.response.status === 500) {
          error_str = "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan).";
        } else {
          error_str = err.response.data.detail.toString();
        }
        newErrors.ean = error_str;
        setStammdatenErrors(newErrors);
        return;
      }
    }
    if (isEan2Changed) {
      setStammdatenInfoMessage("Überprüfe geänderte EAN2");
      try {
        await axios.post(`${backendUrl}/item_creator/check_ean_validity/`, {ean: ean2}, config);
        setIsEan2Changed(false);
      } catch (err) {
        setStammdatenInfoMessage("")
        let error_str: string;
        if (!err.response) {
          error_str = "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar.";
        } else if (err.response.status === 500) {
          error_str = "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan).";
        } else {
          error_str = err.response.data.detail.toString();
        }
        newErrors.ean2 = error_str;
        setStammdatenErrors(newErrors);
        return;
      }
    }

    // Check varnr validity
    if (isVarNrChanged) {
      setStammdatenInfoMessage("Überprüfe geänderte Artikelnummer");
      try {
        await axios.post(`${backendUrl}/item_creator/check_var_nr_validity/`, {var_nr: varNr}, config);
        setIsVarNrChanged(false);
      } catch (err) {
        setStammdatenInfoMessage("")
        let error_str: string;
        if (!err.response) {
          error_str = "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar.";
        } else if (err.response.status === 500) {
          error_str = "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan).";
        } else {
          error_str = err.response.data.detail.toString();
        }
        newErrors.varNr = error_str;
        setStammdatenErrors(newErrors);
        return;
      }
    }

    setStammdatenInfoMessage("")

    // Set all errors in one state update
    setStammdatenErrors(newErrors);
    if (Object.values(newErrors).some((error) => error)) return;

    setSelectedTab(1);

    let stammdatenErrorString = "";
    // Set Manufacturer Id , Lagerauswahl and Flag2 if that hasn't been done already or information changed
    if (!completedRequests.put_stammdaten) {
      setStammdatenInfoMessage("Setze Hersteller, Lagerauswahl und Flagge2")
      const dataStammdaten = {
        itemID: itemID,
        manufacturerID: selectedManufacturer.manufacturerId,
        setFlag1To16: isLagerarticle,
        flag2: (selectedManufacturerKind.value == 3) ? 8 : 11 // Bestellartikel 8, Dropshipping 11
      };
      await axios
        .put(`${backendUrl}/item_creator/put_stammdaten/`, dataStammdaten, config)
        .then(() => {
          setCompletedRequests(prev => ({...prev, put_stammdaten: true}));
          console.log("Put stammdaten successful");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setCompletedRequests(prev => ({...prev, put_stammdaten: false}));
          alert(error_str);
          stammdatenErrorString += error_str + "\n";
        });
    }

    // Set Market Availabilities
    if (!completedRequests.set_availability_to_variation_markets) {
      setStammdatenInfoMessage("Setze Varianten Märkte (unter Verfügbarkeit)")
      const dataMarketAvailabilities = {
        itemID: itemID,
        varID: varID,
        availability: selectedAvailability,
        marketsAvailabilities: useMarketsAvailabilities ?
          selectedMarketsAvailabilities.map(market => market.value) :
          marketsAvailabilities.map(market => market.value),
        hasAlreadyBeenSet: alreadyProcessedPostRequests.set_availability_to_variation_markets
      };
      await axios
        .post(`${backendUrl}/item_creator/set_availability_to_variation_markets/`, dataMarketAvailabilities, config)
        .then(() => {
          setCompletedRequests(prev => ({...prev, set_availability_to_variation_markets: true}));
          setAlreadyProcessedPostRequests(prev => ({...prev, set_availability_to_variation_markets: true}));
          console.log("Put markets availabilities successful");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setCompletedRequests(prev => ({...prev, set_availability_to_variation_markets: false}));
          alert(error_str);
          stammdatenErrorString += error_str + "\n";
        });
    }

    // set Variation Suppliers
    if (!completedRequests.set_variationsupplier) {
      setStammdatenInfoMessage("Setze Lieferanten")
      const dataVariationSupplier = {
        itemID: itemID,
        varID: varID,
        supplierID: selectedManufacturer.supplierId,
        hasAlreadyBeenSet: alreadyProcessedPostRequests.set_variationsupplier
      };
      await axios
        .post(`${backendUrl}/item_creator/set_variation_supplier/`, dataVariationSupplier, config)
        .then(() => {
          setCompletedRequests(prev => ({...prev, set_variationsupplier: true}));
          setAlreadyProcessedPostRequests(prev => ({...prev, set_variationsupplier: true}));
          console.log("post variation supplier successful");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setCompletedRequests(prev => ({...prev, set_variationsupplier: false}));
          alert(error_str);
          stammdatenErrorString += error_str + "\n"
        });
    }

    // set Variation Suppliers
    if (!completedRequests.set_stock) {
      setStammdatenInfoMessage("Lade Warenbestand hoch")
      const dataStock = {
        itemID: itemID,
        varID: varID,
        mainWarehouseID: isLagerarticle ? selectedMainWarehouseId.value : selectedManufacturerKind.value,
        stock: isLagerarticle ? mainWarehouseStock : getStockQuantity(selectedCategories, categories),
        hasAlreadyBeenSet: alreadyProcessedPostRequests.set_stock
      };
      await axios
        .post(`${backendUrl}/item_creator/set_stock/`, dataStock, config)
        .then(() => {
          setCompletedRequests(prev => ({...prev, set_stock: true}));
          setAlreadyProcessedPostRequests(prev => ({...prev, set_stock: true}));
          console.log("Set stock successful");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setCompletedRequests(prev => ({...prev, set_stock: false}));
          alert(error_str);
          stammdatenErrorString += error_str + "\n";
        });
    }

    // Set/ Post Ean(s)
    if (!completedRequests.post_ean1) {
      setStammdatenInfoMessage("Lade EAN hoch");
      const dataEan = {
        itemID: itemID,
        varID: varID,
        EAN: ean,
        pos: 1,
        hasAlreadyBeenSet: alreadyProcessedPostRequests.post_ean1
      };
      await axios
        .post(`${backendUrl}/item_creator/post_ean/`, dataEan, config)
        .then(() => {
          setCompletedRequests(prev => ({...prev, post_ean1: true}));
          setAlreadyProcessedPostRequests(prev => ({...prev, post_ean1: true}));
          console.log("Post EAN successful");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setCompletedRequests(prev => ({...prev, post_ean1: false}));
          alert(error_str);
          stammdatenErrorString += error_str + "\n";
        });
    }

    if (!completedRequests.post_ean2) {
      setStammdatenInfoMessage("Lade EAN2 hoch")
      const dataEan = {
        itemID: itemID,
        varID: varID,
        EAN: ean2,
        pos: 2,
        hasAlreadyBeenSet: alreadyProcessedPostRequests.post_ean1
      };
      await axios
        .post(`${backendUrl}/item_creator/post_ean/`, dataEan, config)
        .then(() => {
          setCompletedRequests(prev => ({...prev, post_ean2: true}));
          setAlreadyProcessedPostRequests(prev => ({...prev, post_ean2: true}));
          console.log("Post EAN2 successful");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setCompletedRequests(prev => ({...prev, post_ean2: false}));
          alert(error_str);
          stammdatenErrorString += error_str + "\n";
        });
    }

    setStammdatenInfoMessage("");
    setStammdatenErrorMessage(stammdatenErrorString);
  };

  const RenderPreisangabenPanel = () => {
    const checkPreisangaben = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setPreisangabenErrorMessage("");
      // Checken ob alles gesetzt ist
      const errorPriceNotSet = "Gebe einen Preis ein."
      const errorPriceEndsWithDot = "Der Preis darf nicht mit . enden."
      const errorPriceTooSmall = "Der Preis muss mindestens 1,3 mal so groß wie der EK Preis sein."
      const newErrors = {
        EK: !priceEK || +priceEK === 0
          ? errorPriceNotSet
          : priceEK.endsWith(".")
            ? errorPriceEndsWithDot
            : null,
        laden: !priceLaden || +priceLaden === 0
          ? errorPriceNotSet
          : priceLaden.endsWith(".")
            ? errorPriceEndsWithDot
            : (+priceLaden / +priceEK <= 1.3)
              ? errorPriceTooSmall
              : null,
        webshop: !priceWebshop || +priceWebshop === 0
          ? errorPriceNotSet
          : priceWebshop.endsWith(".")
            ? errorPriceEndsWithDot
            : (+priceWebshop / +priceEK <= 1.3)
              ? errorPriceTooSmall
              : null,
        verkaufsportalNational: !priceVerkaufsportNational
          ? errorPriceNotSet
          : priceVerkaufsportNational.endsWith(".")
            ? errorPriceEndsWithDot
            : (+priceVerkaufsportNational / +priceEK <= 1.3)
              ? errorPriceTooSmall
              : null,
        OTTO: !priceOTTO || +priceOTTO === 0
          ? errorPriceNotSet
          : priceOTTO.endsWith(".")
            ? errorPriceEndsWithDot
            : (+priceOTTO / +priceEK <= 1.3)
              ? errorPriceTooSmall
              : null,
        verkaufsportalInternational: !priceVerkaufsportInternational || +priceVerkaufsportInternational === 0
          ? errorPriceNotSet
          : priceVerkaufsportInternational.endsWith(".")
            ? errorPriceEndsWithDot
            : (+priceVerkaufsportInternational / +priceEK <= 1.3)
              ? errorPriceTooSmall
              : null,
        eBayAT: !priceEBayAT || +priceEBayAT === 0
          ? errorPriceNotSet
          : priceEBayAT.endsWith(".")
            ? errorPriceEndsWithDot
            : (+priceEBayAT / +priceEK <= 1.3)
              ? errorPriceTooSmall
              : null,
        UVP: !priceUVP || +priceUVP === 0
          ? errorPriceNotSet
          : priceUVP.endsWith(".")
            ? errorPriceEndsWithDot
            : (+priceUVP / +priceEK <= 1.3)
              ? errorPriceTooSmall
              : null,

      }
      setPreisangabenErrors(newErrors);
      if (Object.values(newErrors).some((error) => error)) return;

      setSelectedTab(completedRequests.set_categories ? 3 : 2);

      // Set Prices
      if (!completedRequests.set_prices) {
        const dataStock = {
          varID: varID,
          ladenPrice: priceLaden,
          webshopPrice: priceWebshop,
          nationalPrice: priceVerkaufsportNational,
          internationalPrice: priceVerkaufsportInternational,
          ottoPrice: priceOTTO,
          ebayAtPrice: priceEBayAT,
          uvpPrice: priceUVP,
        };
        await axios
          .post(`${backendUrl}/item_creator/set_prices/`, dataStock, config)
          .then(() => {
            setCompletedRequests(prev => ({...prev, set_prices: true}));
            console.log("Set prices successful");
          })
          .catch((err) => {
            const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
              : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
                : err.response.data?.detail?.toString();
            setCompletedRequests(prev => ({...prev, set_prices: false}));
            alert(error_str);
            setPreisangabenErrorMessage(error_str);
          });
      }
    };

    const info_ek = "Der Einkaufspreis (EK) ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „EK“ zu finden.";
    const info_ladenPreis = "Der Ladenpreis ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „Laden / Webshop“ zu finden.";
    const info_webshopPreis = "Der Webshoppreis ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „Laden / Webshop“ zu finden.";
    const info_nationalPreis = "Der Preis für Verkaufsportale National ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „VK National / OTTO“ zu finden.";
    const info_ottoPreis = "Der OTTO-Preis ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „VK National / OTTO“ zu finden.";
    const info_internationalPreis = "Der Preis für Verkaufsportale International ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „UVP/VK International“ zu finden.";
    const info_ebayAtPreis = "Der eBay AT Preis ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „UVP/VK International“ zu finden.";
    const info_uvpInfo = "Der UVP ist – sofern nicht anders vorgegeben - in der Excel-Preisliste in der Spalte mit der Bezeichnung „UVP/VK International“ zu finden.";

    return <div className="Tab">
      <Stack {...stackProps}>
        <CustomTextField
          label={"EK"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          value={priceEK}
          manualError={!!preisangabenErrors.EK}
          errorText={preisangabenErrors.EK || ""}
          onChange={(e) => {
            setPriceEK(e.target.value);
            setCompletedRequests(prev => ({...prev, set_availability_masse_mainwarehouse_ek_price: false}));
          }}
        />
        <HooverTooltip content={info_ek}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField
          label={"Laden"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          value={priceLaden}
          manualError={!!preisangabenErrors.laden}
          errorText={preisangabenErrors.laden || ""}
          onChange={(e) => {
            setPriceLaden(e.target.value);
            setCompletedRequests(prev => ({...prev, set_prices: false}));
          }}
        />
        <HooverTooltip content={info_ladenPreis}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField
          label={"Webshop"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          value={priceWebshop}
          manualError={!!preisangabenErrors.webshop}
          errorText={preisangabenErrors.webshop || ""}
          onChange={(e) => {
            setPriceWebshop(e.target.value);
            setCompletedRequests(prev => ({...prev, set_prices: false}));
          }}
        />
        <HooverTooltip content={info_webshopPreis}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField
          label={"Verkaufsportal National"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          value={priceVerkaufsportNational}
          manualError={!!preisangabenErrors.verkaufsportalNational}
          errorText={preisangabenErrors.verkaufsportalNational || ""}
          onChange={(e) => {
            setPriceVerkaufsportNational(e.target.value);
            setCompletedRequests(prev => ({...prev, set_prices: false}));
          }}
        />
        <HooverTooltip content={info_nationalPreis}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField
          label={"OTTO"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          value={priceOTTO}
          manualError={!!preisangabenErrors.OTTO}
          errorText={preisangabenErrors.OTTO || ""}
          onChange={(e) => {
            setPriceOTTO(e.target.value);
            setCompletedRequests(prev => ({...prev, set_prices: false}));
          }}
        />
        <HooverTooltip content={info_ottoPreis}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField
          label={"Verkaufsportal International"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          value={priceVerkaufsportInternational}
          manualError={!!preisangabenErrors.verkaufsportalInternational}
          errorText={preisangabenErrors.verkaufsportalInternational || ""}
          onChange={(e) => {
            setPriceVerkaufsportInternational(e.target.value);
            setCompletedRequests(prev => ({...prev, set_prices: false}));
          }}
        />
        <HooverTooltip content={info_internationalPreis}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField
          label={"eBay AT"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          manualError={!!preisangabenErrors.eBayAT}
          errorText={preisangabenErrors.eBayAT || ""}
          value={priceEBayAT} onChange={(e) => {
          setPriceEBayAT(e.target.value);
          setCompletedRequests(prev => ({...prev, set_prices: false}));
        }}
        />
        <HooverTooltip content={info_ebayAtPreis}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField
          label={"UVP"}
          is_required={true}
          inputMode={"decimal"}
          endAdornment="€"
          value={priceUVP}
          manualError={!!preisangabenErrors.UVP}
          errorText={preisangabenErrors.UVP || ""}
          onChange={(e) => {
            setPriceUVP(e.target.value);
            setCompletedRequests(prev => ({...prev, set_prices: false}));
          }}
        />
        <HooverTooltip content={info_uvpInfo}/>
      </Stack>
      <Stack {...stackProps}>
        <SubmitButton
          label={"Speichern und weiter"}
          sx={{width: "50%", minWidth: "50%"}}
          onClick={checkPreisangaben}
        />
        <EmptyTooltipPlaceholder/>
      </Stack>
      {renderErrorMessage(preisangabenErrorMessage)}
    </div>;
  };

  const RenderStammdatenPanel = () => {
    const info_manufacturer = "Der Hersteller wird vor Beginn des Einstellens vorgegeben. ";
    const info_manufacturerKind = "Trage hier ein, ob dieser Artikel ein Bestell- oder Dropshippingartikel ist.";
    const info_availability = "Trage hier die entsprechende Verfügbarkeit ein. Diese ist entweder der Preis-/Stammdatenliste zu entnehmen oder wird vor dem Einstellen des jeweiligen Herstellers vorgegeben.";
    const isGartengartenOnly = selectedShops.length === 1 && selectedShops[0].value === 73461;

    return <div className="Tab" style={{gap: "15px", justifyContent: "center"}}>
      <Stack {...stackProps}>
        <SearchableSelect inputLabel={"Hersteller"} items={manufacturers} value={selectedManufacturer}
          setValue={(newManufacturer) => {
            const prevHadConstraint = selectedManufacturer?.value in manufacturerMarketsAvailabilityConstraints;
            const newHasConstraint = newManufacturer.value in manufacturerMarketsAvailabilityConstraints;

            // Only apply manufacturer constraints if Natur24 is selected
            if (!isGartengartenOnly) {
              if (newHasConstraint) { // New one has a constraint -> select the corresponding markets availabilities
                setUseMarketsAvailabilities(true);
                setIsDisableUseMarketsAvailabilities(true);
                setSelectedMarketsAvailabilities(manufacturerMarketsAvailabilityConstraints[newManufacturer.value]);
                setCompletedRequests(prev => ({
                  ...prev,
                  put_stammdaten: false,
                  set_variationsupplier: false,
                  set_availability_to_variation_markets: false
                }));
              } else if (prevHadConstraint && !newHasConstraint) { // Old one has a constraint but new one doesn't -> Select all markets availabilities
                setUseMarketsAvailabilities(false);
                setIsDisableUseMarketsAvailabilities(false);
                setSelectedMarketsAvailabilities(marketsAvailabilities);
                setCompletedRequests(prev => ({
                  ...prev,
                  put_stammdaten: false,
                  set_variationsupplier: false,
                  set_availability_to_variation_markets: false
                }));
              } else { // neither old nor new manufacturer has a constraint -> Nothing regarding the selected markets availabilities change but only the requests that use the selectedManufacturer
                setCompletedRequests(prev => ({
                  ...prev,
                  put_stammdaten: false,
                  set_variationsupplier: false
                }));
              }
            } else { // If Gartengarten24, just reset the standard requests, leave markets alone
              setCompletedRequests(prev => ({...prev, put_stammdaten: false, set_variationsupplier: false}));
            }
            // Set newly selected manufacturer
            setSelectedManufacturer(newManufacturer);
            // if item was copied, changing the manufacturer erases the manufacturerkind
            if (isCopyArticle) {
              setSelectedManufacturerKind(null)
            }
          }} sx={{width: "50%"}}/>
        <HooverTooltip content={info_manufacturer}/>
      </Stack>
      {renderErrorMessage(stammdatenErrors.selectedManufacturer)}
      <Stack {...stackProps}>
        <SearchableSelect inputLabel={"Herstellerart"}
                          items={[{value: 3, label: "Kommissionslager Bestellartikel"}, {
                            value: 6,
                            label: "Dropshipping"
                          }]} value={selectedManufacturerKind} setValue={(value) => {
          setSelectedManufacturerKind(value);
          setCompletedRequests(prev => ({
            ...prev,
            put_stammdaten: false,
            set_stock: false,
            set_availability_masse_mainwarehouse_ek_price: false
          }));
        }} sx={{width: "50%"}}/>
        <HooverTooltip content={info_manufacturerKind}/>
      </Stack>
      {renderErrorMessage(stammdatenErrors.selectedManufacturerKind)}
      <div style={{display: "flex", flexDirection: "row", gap: "15px", alignItems: "center"}}>
        <CustomCheckbox label={"Lagerartikel"} value={isLagerarticle} onChange={(checked) => {
          setIslagerArticle(checked);
          setCompletedRequests(prev => ({
            ...prev,
            put_stammdaten: false,
            set_stock: false,
            set_availability_masse_mainwarehouse_ek_price: false
          }));
        }} is_disabled={false}/>
        <CustomTextField label={"Anzahl"} value={mainWarehouseStock}
                         onChange={(e) => {
                           setMainWarehouseStock(e.target.value);
                           setCompletedRequests(prev => ({...prev, set_stock: false}));
                         }}
                         inputMode={"integer"}
                         is_required={isLagerarticle}
                         manualError={!!stammdatenErrors.mainWarehouseStock}
                         errorText={isLagerarticle && stammdatenErrors.mainWarehouseStock || ""}
                         is_disabled={!isLagerarticle}
                         sx={{width: "100px"}}/>
        <SearchableSelect inputLabel={"Lager"}
                          items={[{value: 4, label: "Ausstellung Hauptladen"}, {
                            value: 7,
                            label: "Schlafwelt (demnächst Küchenwelt)"
                          }, {value: 1, label: "Lager Pfullingen"}]}
                          value={selectedMainWarehouseId} setValue={(value) => {
          setSelectedMainWarehouseId(value);
          setCompletedRequests(prev => ({
            ...prev,
            set_stock: false,
            set_availability_masse_mainwarehouse_ek_price: false
          }));
        }}
                          is_required={isLagerarticle}
                          is_disabled={!isLagerarticle}
                          sx={{width: "300px"}}/>
      </div>
      {isLagerarticle && renderErrorMessage(stammdatenErrors.selectedMainWarehouseId)}
      <div style={{
        display: "flex",
        flexDirection: "row",
        gap: "15px",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <CustomSelect
          label="Verfügbarkeitsstufe"
          id="Verfügbarkeitsstufe"
          items={[{value: 1, label: "1"}, {value: 2, label: "2"}, {value: 3, label: "3"}, {
            value: 4,
            label: "4"
          }, {value: 5, label: "5"}, {value: 6, label: "6"}, {value: 7, label: "7"}, {value: 8, label: "8"}, {
            value: 9,
            label: "9"
          }, {value: 10, label: "10"}]}
          inputLabel="Verfügbarkeitsstufe"
          is_required={true}
          value={selectedAvailability}
          setValue={setSelectedAvailability}
          onChange={() => {
            setCompletedRequests(prev => ({
              ...prev,
              set_availability_to_variation_markets: false,
              set_availability_masse_mainwarehouse_ek_price: false
            }));
          }}
          sx={{width: "200px", minWidth: "200px"}}
          manualError={!!stammdatenErrors.selectedAvailability}
        />
        <img
          src="/availPic.png"
          alt="Verschiedene Lieferzeit Optionen"
          style={{width: "50%", height: "35px", objectFit: "cover"}}
        />
        <HooverTooltip content={info_availability}/>
      </div>
      {renderErrorMessage(stammdatenErrors.selectedAvailability)}
      <Stack {...stackProps}>
        <CustomTextField label={"Artikelnr. Hersteller"}
                         value={varNr}
                         onChange={(e) => {
                           setVarNr(e.target.value);
                           setIsVarNrChanged(true);
                           setCompletedRequests(prev => ({
                             ...prev,
                             set_availability_masse_mainwarehouse_ek_price: false
                           }));
                         }}
                         inputMode={"text"}
                         is_required={true}
                         manualError={!!stammdatenErrors.varNr}
                         errorText={stammdatenErrors.varNr || ""}
                         is_disabled={true}
                         addDisableButton={true}
        />
        <EmptyTooltipPlaceholder/>
      </Stack>
      {renderErrorMessage(stammdatenErrors.varNr)}
      <Stack {...stackProps}>
        <CustomTextField label={"EAN"}
                         value={ean}
                         onChange={(e) => {
                           handleEanChange(e, setEan, setStammdatenErrors, "ean");
                           setIsEanChanged(true);
                           setCompletedRequests(prev => ({...prev, post_ean1: false}));
                         }}
                         inputMode={"integer"}
                         is_required={true}
                         manualError={!!stammdatenErrors.ean}
                         errorText={stammdatenErrors.ean || ""}
                         is_disabled={true}
                         addDisableButton={true}
        />
        <EmptyTooltipPlaceholder/>
      </Stack>
      {renderErrorMessage(stammdatenErrors.ean)}
      <Stack {...stackProps}>
        <CustomTextField label={"EAN 2"}
                         value={ean2}
                         onChange={(e) => {
                           handleEanChange(e, setEan2, setStammdatenErrors, "ean2");
                           setIsEan2Changed(true);
                           setCompletedRequests(prev => ({...prev, post_ean2: false}));
                         }}
                         inputMode={"integer"}
                         is_required={false}
                         manualError={!!stammdatenErrors.ean2}
                         errorText={stammdatenErrors.ean2 || ""}
        />
        <EmptyTooltipPlaceholder/>
      </Stack>
      {renderErrorMessage(stammdatenErrors.ean2)}
      <div style={{
        display: "flex",
        flexDirection: "row",
        gap: "15px",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <CustomCheckbox label={"Marktverfügbarkeiten"} value={useMarketsAvailabilities}
                        onChange={(checked) => {
                          setUseMarketsAvailabilities(checked);
                          setCompletedRequests(prev => ({
                            ...prev,
                            set_availability_to_variation_markets: false
                          }));
                        }} is_disabled={isDisableUseMarketsAvailabilities || isGartengartenOnly}/>
        <SearchableMultiSelect items={marketsAvailabilities} inputLabel="Märkte" selectAllOption={true}
                               selectAllInitially={true}
                               value={selectedMarketsAvailabilities} setValue={(newVal) => {
          setSelectedMarketsAvailabilities(newVal);
          setCompletedRequests(prev => ({...prev, set_availability_to_variation_markets: false}));
        }}
                               disabled={!useMarketsAvailabilities || isDisableUseMarketsAvailabilities}
                               hasInitialized={hasInitializedMarketsSelect}
                               setHasInitialized={setHasInitializedMarketsSelect}/>
      </div>
      {isGartengartenOnly && (
        <p style={{ color: "var(--naturGreen)", margin: 0}}>
          Für Gartengarten24 sind die Marktverfügbarkeiten fest auf WebShop und WEB-API vorgegeben.
        </p>
      )}
      {useMarketsAvailabilities && renderErrorMessage(stammdatenErrors.marketsAvailabilities)}
      <SubmitButton label={"Speichern und weiter"} sx={{width: "50%", minWidth: "50%"}}
                    onClick={checkStammdaten}/>
      {renderInfoMessage(stammdatenInfoMessage)}
      {renderErrorMessage(stammdatenErrorMessage)}
    </div>;
  };

  const RenderCategoryPanel = () => {
    const checkCategories = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setCategoryPanelError("");

      // Check whether the selected categories are actually the deepest categories
      const categoryPaths = categories.map(category => category.path);
      const selectedCategoriesPaths = Array.from(selectedCategories.values());
      const deepestSelectedCategoryPaths = getLeafCategories(selectedCategoriesPaths);
      const hasNonDeepestSelected = deepestSelectedCategoryPaths.some(cat => !isDeepestCategory(cat, categoryPaths));

      if (selectedCategories.size === 0) {
        setCategoryPanelError("Wähle bitte mindestens eine Kategorie!");
      } else if (hasNonDeepestSelected) {
        setCategoryPanelError("Wähle bitte immer die tiefstmöglichen Kategorien aus!");
      } else {
        setSelectedTab(3);
        // Set Categories
        if (!completedRequests.set_categories) {
          const dataStock = {
            itemID: itemID,
            varID: varID,
            categories: Array.from(selectedCategories.keys()),
            defaultCategory: getDeepestCategoryID(), // Important in case of deletion as this one should remain
            hasAlreadyBeenSet: false // Previously this was alreadyProcessedPostRequests.set_categories but now categories can only be changed in the beginning
          };
          await axios
            .post(`${backendUrl}/item_creator/set_categories/`, dataStock, config)
            .then(() => {
              setCompletedRequests(prev => ({...prev, set_categories: true}));
              console.log("Set stock successful");
            })
            .catch((err) => {
              const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
                : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
                  : err.response.data?.detail?.toString();
              setCompletedRequests(prev => ({...prev, set_categories: false}));
              alert(error_str);
              setPreisangabenErrorMessage(error_str);
            });
        }
      }
    };

    return (
      <div className="Tab">
        <SubmitButton label={"Erneut versuchen, Kategorien zu setzen"} sx={{width: "50%", minWidth: "50%"}}
                      onClick={checkCategories}/>
        {renderErrorMessage(categoryPanelError)}
      </div>);
  };

  const RenderMetricsPanel = () => {
    const checkMetrics = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setMasseErrorMessage("");

      const newErrors = {
        lengthOrDepth: !lengthOrDepth ? `Bitte gebe die ${isLengthUsed ? "Länge" : "Tiefe"} an` :
          lengthOrDepth.endsWith(".") ? "Die Länge/ Tiefe darf nicht mit . enden!" :
            lengthOrDepth == "0" ? "Die Länge/ Tiefe muss eine Zahl > 0 sein!" : null,
        width: !width ? "Bitte gebe die Breite an" :
          width.endsWith(".") ? "Die Breite darf nicht mit . enden!" :
            width == "0" ? "Die Breite muss eine Zahl > 0 sein!" : null,
        height: !height ? "Bitte gebe die Höhe an" :
          height.endsWith(".") ? "Die Höhe darf nicht mit . enden!" :
            height == "0" ? "Die Höhe muss eine Zahl > 0 sein!" : null,
        weight: weight && isNaN(Number(weight)) ? "Das Gewicht ist keine gültige Zahl" :
          weight.endsWith(".") ? "Das Gewicht darf nicht mit . enden!" :
            weight == "0" ? "Das Gewicht muss eine Zahl > 0 sein!" : null,
        selectedWoodTypesOrMaterials: (!selectedWoodTypes?.length && !selectedMaterials?.length) ? "Bitte wähle entweder mindestens eine Holzart oder mindestens ein Material aus" : null,
        selectedColors: !selectedColors?.length ? "Bitte wähle mindestens eine Farbe aus" : null,
        selectedLiegeFlaeche: (useLiegeFlaeche && !selectedLiegeFlaeche) ? "Bitte wähle die Liegefläche aus" : null,
        selectedHaerteGrad: (useHaerteGrad && !selectedHaerteGrad) ? "Bitte wähle den Härtegrad aus" : null,
      };

      // Width, Lenght/Depth, Height in MM umwandeln
      const widthMM = Number(width) * 10;
      const lengthOrDepthMM = Number(lengthOrDepth) * 10;
      const heightMM = Number(height) * 10;
      // Weight in G umwandeln
      const weightG = weight ? Number(weight) * 1000 : null;
      console.log(widthMM, lengthOrDepthMM, heightMM, weightG);

      // Set all errors in one state update
      if (Object.values(newErrors).some((error) => error)) {
        setMasseErrors(newErrors);
        return;
      }

      setMasseErrors(newErrors);

      // Insert or change the item in the produktdaten
      const produktdatenMasseEntry = (isLengthUsed
          ? `Maße: Länge x Breite x Höhe: ca. ${lengthOrDepth} x ${width} x ${height} cm`
          : `Maße: Breite x Höhe x Tiefe: ca. ${width} x ${height} x ${lengthOrDepth} cm`
      ).replace(/(\d+)\.(\d+)/g, "$1,$2"); //Replace all . between two numbers with ,
      setProduktdaten((prev) => {
        const index = prev.findIndex((item) => item.startsWith("Maße"));
        setCompletedRequests(prev => ({...prev, set_texts: false}));
        if (index !== -1) { // Update existing entry
          const newList = [...prev];
          newList[index] = produktdatenMasseEntry;
          return newList;
        } else { // Add to the beginning
          return [produktdatenMasseEntry, ...prev];
        }
      });
      const selectedWoodTypesList = selectedWoodTypes.map((m) => m.label);
      const selectedMaterialsList = selectedMaterials.map((m) => m.label);
      // Combine into a comma-separated string
      const materialString = selectedWoodTypesList.length && selectedMaterialsList.length
        ? `Material: ${selectedWoodTypesList.join(", ")}, ${selectedMaterialsList.join(", ")}`
        : selectedWoodTypesList.length
          ? `Material: ${selectedWoodTypesList.join(", ")}`
          : selectedMaterialsList.length
            ? `Material: ${selectedMaterialsList.join(", ")}`
            : "";
      if (materialString) {
        setProduktdaten((prev) => {
          const index = prev.findIndex((item) => item.startsWith("Material"));
          if (index !== -1) {
            const newList = [...prev];
            newList[index] = materialString;
            return newList;
          } else {
            return [...prev, materialString]; // Add at the end
          }
        });
        setCompletedRequests(prev => ({...prev, set_texts: false}));
      }
      const farbeList = selectedColors.map((color) => color.label).join(", ");
      if (farbeList) {
        const produktdatenFarbeEntry = `Farbe: ${farbeList}`;
        setProduktdaten((prev) => {
          const index = prev.findIndex((item) => item.startsWith("Farbe"));
          if (index !== -1) {
            const newList = [...prev];
            newList[index] = produktdatenFarbeEntry;
            return newList;
          } else {
            return [...prev, produktdatenFarbeEntry]; // Add at the end
          }
        });
        setCompletedRequests(prev => ({...prev, set_texts: false}));
      }
      const isWeightEntered = weight !== "";
      const possibleWeightPosition = produktdaten.findIndex((item) => item.startsWith("Gewicht"));
      if (!isWeightEntered) { // If no weight entered, add the default "Gewicht" suggestion to the suggestions list
        setProduktdatenSuggestions(["Gewicht: ca. [Gewicht] kg", ...defaultProduktDatenSuggestions]);
        if (possibleWeightPosition !== -1) { // If "Gewicht" entry exists, remove it
          setProduktdaten((prev) => {
            setCompletedRequests(prev => ({...prev, set_texts: false}));
            const newList = [...prev];
            newList.splice(possibleWeightPosition, 1);
            return newList;
          });
        }
      } else { // If weight is entered, add or update the "Gewicht" entry
        const produktdatenGewichtEntry = `Gewicht: ca. ${weight.replace(".", ",")} kg`;

        setProduktdaten((prev) => {
          if (possibleWeightPosition !== -1) {
            setCompletedRequests(prev => ({...prev, set_texts: false}));
            // Update existing "Gewicht" entry
            const newList = [...prev];
            newList[possibleWeightPosition] = produktdatenGewichtEntry;
            return newList;
          } else {
            // Add "Gewicht" entry if not found
            const newList = [...prev];
            newList.splice(3, 0, produktdatenGewichtEntry); // Add at index 3 or another desired position
            return newList;
          }
        });
        setProduktdatenSuggestions(defaultProduktDatenSuggestions);
      }
      if (useLiegeFlaeche) {
        const formattedLabel = selectedLiegeFlaeche.label.replace(/(\d)\s*x\s*(\d)/, '$1 x $2');
        const produktdatenLiegeFlaecheEntry = `Liegefläche: ${formattedLabel} cm`;

        setProduktdaten((prev) => {
          const index = prev.findIndex((item) => item.startsWith("Liegefläche"));
          if (index !== -1) {
            const newList = [...prev];
            newList[index] = produktdatenLiegeFlaecheEntry;
            return newList;
          } else {
            return [...prev, produktdatenLiegeFlaecheEntry]; // Add at the end
          }
        });
        setCompletedRequests(prev => ({...prev, set_texts: false}));
      }
      // Set previewDescription
      const metricsDescription = (isLengthUsed
          ? `. Maße: ca.: Länge x Breite x Höhe: ca. ${lengthOrDepth} x ${width} x ${height} cm`
          : `. Maße: ca.: Breite x Höhe x Tiefe: ca. ${width} x ${height} x ${lengthOrDepth} cm`
      ).replace(/(\d+)\.(\d+)/g, "$1,$2"); // Replace all . between two numbers with ,
      // 1) Decide what “base” text to start from:
      //    – if previewDescription exists, use that
      //    – otherwise if it’s a copy-article, use the copied preview text
      //    – else start from an empty string
      const base = previewDescription?.trim().length > 0
        ? previewDescription
        : (isCopyArticle && copyItemPreviewDescription)
          ? copyItemPreviewDescription
          : "";
      // Strip off any existing “. Maße:” block (and everything after it), if present
      //    – if no “. Maße:” is present, replace() does nothing, so the whole base string is kept
      const prefix = base.replace(/(\. Maße:)[\s\S]*$/, "");

      const newPreview = prefix + metricsDescription;
      if (newPreview !== previewDescription) {
        setPreviewDescription(newPreview);
        setCompletedRequests(prev => ({...prev, set_texts: false}));
      }

      setSelectedTab(4);

      // Set Variation Properties
      if (!completedRequests.set_variation_properties) {
        const dataVariationProperties = {
          varID: varID,
          lengthOrDepth: lengthOrDepth,
          isLengthUsed: isLengthUsed,
          width: width,
          height: height,
          weight: weight,
          selectedColorIds: selectedColors.map(color => color.value),
          selectedWoodTypes: selectedWoodTypes.map(woodType => woodType.value),
          selectedMaterialIds: selectedMaterials.map(material => material.value),
          ottoCategoryId: selectedOttoCat ? selectedOttoCat.ottoCatId : null,
          liegeflaecheId: useLiegeFlaeche ? selectedLiegeFlaeche.value : null,
          haertegradId: useHaerteGrad ? selectedHaerteGrad.value : null,
          hasAlreadyBeenSet: alreadyProcessedPostRequests.set_variation_properties,
        };
        await axios
          .post(`${backendUrl}/item_creator/set_variation_properties/`, dataVariationProperties, config)
          .then(() => {
            setCompletedRequests(prev => ({...prev, set_variation_properties: true}));
            setAlreadyProcessedPostRequests(prev => ({...prev, set_variation_properties: true}))
            console.log("Set Variation Properties successful");
          })
          .catch((err) => {
            const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
              : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
                : err.response.data?.detail?.toString();
            setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
            alert(error_str);
            setPreisangabenErrorMessage(error_str);
          });
      }
    };

    const info_length_depth = "Bitte wähle die passende Länge bzw. Tiefe aus. Dieses ist entweder der Preis-/Stammdatenliste oder auf der Herstellerwebseite zu entnehmen.";
    const info_width = "Bitte wähle die passende Breite aus. Dieses ist entweder der Preis-/Stammdatenliste oder auf der Herstellerwebseite zu entnehmen.";
    const info_height = "Bitte wähle die passende Höhe aus. Dieses ist entweder der Preis-/Stammdatenliste oder auf der Herstellerwebseite zu entnehmen.";
    const info_weight = "Bitte wähle das passende Gewicht aus. Dieses ist entweder der Preis-/Stammdatenliste oder auf der Herstellerwebseite zu entnehmen.";
    const info_material = "Bitte wähle das treffende Material aus. Mindestens eine Option aus Material oder Holzart muss ausgewählt werden. Dieses ist entweder der Preis-/Stammdatenliste oder auf der Herstellerwebseite zu entnehmen.";
    const info_color = "Bitte wähle treffende Farben. Tipp: Versetze dich in die Lage des Kunden, nach welcher Farbe würdest du im Internet diesen Artikel suchen?";
    const info_liegeflaeche = "Bitte wähle die Liegefläche des Artikels aus";
    const info_wood = "Bitte wähle die passende Holzart aus. Mindestens eine Option aus Material oder Holzart muss ausgewählt werden. Diese ist entweder der Preis-/Stammdatenliste oder auf der Herstellerwebseite zu entnehmen.";
    const info_haertegrad = "Bitte wähle den Härtgrad des Artikels aus";

    return <div className="Tab">
      <Stack {...stackProps}>
        <CustomTextField label={isLengthUsed ? "Länge in cm" : "Tiefe in cm"} inputMode={"singledecimal"}
                         is_required={true}
                         value={lengthOrDepth} onChange={(e) => {
          setLengthOrDepth(e.target.value);
          setCompletedRequests(prev => ({
            ...prev,
            set_variation_properties: false,
            set_availability_masse_mainwarehouse_ek_price: false
          }));
        }}
                         manualError={!!masseErrors.lengthOrDepth} errorText={masseErrors.lengthOrDepth || ""}/>
        <HooverTooltip content={info_length_depth}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"Breite in cm"} inputMode={"singledecimal"} is_required={true}
                         value={width} onChange={(e) => {
          setWidth(e.target.value);
          setCompletedRequests(prev => ({
            ...prev,
            set_variation_properties: false,
            set_availability_masse_mainwarehouse_ek_price: false
          }));
        }}
                         manualError={!!masseErrors.width} errorText={masseErrors.width || ""}/>
        <HooverTooltip content={info_width}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"Höhe in cm"} inputMode={"singledecimal"} is_required={true}
                         value={height} onChange={(e) => {
          setHeight(e.target.value);
          setCompletedRequests(prev => ({
            ...prev,
            set_variation_properties: false,
            set_availability_masse_mainwarehouse_ek_price: false
          }));
        }}
                         manualError={!!masseErrors.height} errorText={masseErrors.height || ""}/>
        <HooverTooltip content={info_height}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"Gewicht in kg"} inputMode={"singledecimal"} is_required={false}
                         value={weight} onChange={(e) => {
          setWeight(e.target.value);
          setCompletedRequests(prev => ({
            ...prev,
            set_variation_properties: false,
            set_availability_masse_mainwarehouse_ek_price: false
          }));
        }}
                         manualError={!!masseErrors.weight} errorText={masseErrors.weight || ""}/>
        <HooverTooltip content={info_weight}/>
      </Stack>
      <Stack {...stackProps}>
        <SearchableMultiSelect items={woodTypes} inputLabel="Holzart" selectAllOption={false}
                               selectAllInitially={false}
                               value={selectedWoodTypes} setValue={(newVal) => {
          setSelectedWoodTypes(newVal);
          setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
        }} sx={{minWidth: "50%"}} closeOnSelection={true}/>
        <HooverTooltip content={info_wood}/>
      </Stack>
      <Stack {...stackProps}>
        <SearchableMultiSelect items={materials} inputLabel="Material"
                               selectAllOption={false}
                               selectAllInitially={false}
                               value={selectedMaterials} setValue={(newVal) => {
          setSelectedMaterials(newVal);
          setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
        }} sx={{minWidth: "50%"}} closeOnSelection={true}/>
        <HooverTooltip content={info_material}/>
      </Stack>
      {renderErrorMessage(masseErrors.selectedWoodTypesOrMaterials)}
      <Stack {...stackProps}>
        <SearchableMultiSelect items={colors} inputLabel="Farben"
                               selectAllOption={false} selectAllInitially={false}
                               value={selectedColors} setValue={(newVal) => {
          setSelectedColors(newVal);
          setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
        }} sx={{minWidth: "50%"}} closeOnSelection={true}/>
        <HooverTooltip content={info_color}/>
      </Stack>
      {renderErrorMessage(masseErrors.selectedColors)}
      <Stack {...stackProps}>{useLiegeFlaeche &&
          <SearchableSelect items={liegeFlaechen} inputLabel="Liegefläche" selectAllOption={false}
                            selectAllInitially={false}
                            value={selectedLiegeFlaeche} setValue={(value) => {
            setSelectedLiegeFlaeche(value);
            setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
          }} sx={{width: "50%"}}/>}
        {useLiegeFlaeche && <HooverTooltip content={info_liegeflaeche}/>}
      </Stack>
      {useLiegeFlaeche && renderErrorMessage(masseErrors.selectedLiegeFlaeche)}
      <Stack {...stackProps}
             sx={{...(stackProps?.sx || {}), marginLeft: '500px'}}>{useHaerteGrad &&
          <SearchableSelect items={haerteGrade} inputLabel="Härtegrad (Wenn Artikel Matratze ist)"
                            selectAllOption={false} selectAllInitially={false}
                            value={selectedHaerteGrad} setValue={(value) => {
            setSelectedHaerteGrad(value);
            setCompletedRequests(prev => ({...prev, set_variation_properties: false}));
          }} sx={{width: "50%"}}/>}
        {useHaerteGrad && <HooverTooltip content={info_haertegrad}/>}
      </Stack>
      {useHaerteGrad && renderErrorMessage(masseErrors.selectedHaerteGrad)}
      <Stack {...stackProps}>
        <SubmitButton label={"Speichern und weiter"}
                      sx={{width: "50%", minWidth: "50%"}}
                      onClick={checkMetrics}/>
        <EmptyTooltipPlaceholder/>
      </Stack>
      {renderErrorMessage(masseErrorMessage)}
    </div>
      ;
  };

  function generateHtmlPreview() {
    const generateTreeHtmlList = (nodes: TreeItemNode[]) => {
      if (!nodes || nodes.length === 0) return "";

      let listHTML = "<ul>";
      nodes.forEach((node) => {
        listHTML += "<li><span style=font-family:arial,helvetica,sans-serif>" + node.label + "</span>";
        if (node.children && node.children.length > 0) {
          listHTML += generateTreeHtmlList(node.children);
        }

        listHTML += "</li>"
      });

      listHTML += "</ul>";
      return listHTML;
    };

    let previewText = "<h1><span style=font-family:arial,helvetica,sans-serif>" + ueberschrift1 + "</span></h1>" + "<p>&nbsp;</p>";
    previewText += "<p><span style=font-family:arial,helvetica,sans-serif><strong>Lieferumfang:</strong></span></p>";
    previewText += "<ul>" + lieferumfang.map((element) =>
      "<li><span style=font-family:arial,helvetica,sans-serif>" + element.quantity + "x " + element.text + "</span></li>"
    ).join("") + "</ul>" + "<p>&nbsp;</p>";
    previewText += "<p><span style=font-family:arial,helvetica,sans-serif><strong>Produktdaten:</strong></span></p>";
    previewText += "<ul>" + produktdaten.map((element) =>
      "<li><span style=font-family:arial,helvetica,sans-serif>" + element + "</span></li>"
    ).join("") + "</ul>" + "<p>&nbsp;</p>";
    previewText += "<p><span style=font-family:arial,helvetica,sans-serif><strong>Produktdetails:</strong></span></p>";
    previewText += generateTreeHtmlList(produktdetails) + "<p>&nbsp;</p>";
    previewText += "<h2><span style=font-family:arial,helvetica,sans-serif>" + ueberschrift2 + "</span></h2>";
    previewText += articleText.split("\n").map(
      (paragraph) => "<p style=text-align:justify><span style=font-family:arial,helvetica,sans-serif>" + paragraph + "</span></p>"
    ).join("");
    return previewText;
    // setHtmlPreview(previewText);
  }

  const checkProdukttexte = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setProduktTexteInfoMessage("");
      setProduktTexteErrorMessage("");
      // Checken ob alles gesetzt ist
      const newErrors = {
        name1And2: null,
        name3: null,
        previewDescription: null,
        ueberschrift1: null,
        lieferumfang: null,
        produktdaten: null,
        produktdetails: null,
        ueberschrift2: null,
        articleText: null,
        produktTexteErrors: null
      };

      // refs to scroll to when there is an error after pressing speicher und weiter button
      const errorRefs = {
        name1And2: name1And2Ref,
        name3: name3Ref,
        previewDescription: previewDescriptionRef,
        ueberschrift1: ueberschrift1Ref,
        lieferumfang: lieferumfangRef,
        produktdaten: produktdatenRef,
        produktdetails: produktdetailsRef,
        ueberschrift2: ueberschrift2Ref,
        articleText: articleTextRef,
        produktTexteErrors: produktTexteErrorsRef
      }
      // Texte
      // name1And2 == name3
      if (name1And2.trim() === name3.trim()) {
        newErrors.name1And2 = "Name 1 und Name 3 müssen verschieden sein!";
        newErrors.name3 = "Name 1 und Name 3 müssen verschieden sein!"
      }
      // name1And2
      if (name1And2.trim().length < 20 || name1And2.trim().length > 80) {
        newErrors.name1And2 = "Der Name 1 und 2 muss einen Text zwischen " + 20 + " und " + 80 + " Zeichen enthalten!";
      }
      // name3
      if (name3.trim().length < 20 || name3.trim().length > 70) {
        newErrors.name3 = "Der Name 3 muss einen Text zwischen " + 20 + " und " + 70 + " Zeichen enthalten!";
      }
      // Textüberprüfung auf verbotene Wörter
      if (!isIgnoreInvalidWords) {
        const checkForbiddenWords = (text) => {
          const normalizedText = text.toLowerCase().replace(/[\s\-_\/]+/g, ""); // Normalize text: remove spaces, lowercase
          const foundWords = invalidWords.filter(word => {
            const normalizedWord = word.toLowerCase().replace(/\s+/g, ""); // Normalize forbidden words
            return normalizedText.includes(normalizedWord); // Check if forbidden word exists in normalized text
          });
          return foundWords.length > 0 ? `Folgende verwendete Wörter sind verboten: ${foundWords.join(", ")}` : null;
        };

        newErrors.name1And2 = checkForbiddenWords(name1And2) || newErrors.name1And2;
        newErrors.name3 = checkForbiddenWords(name3) || newErrors.name3;
        newErrors.previewDescription = checkForbiddenWords(previewDescription) || newErrors.previewDescription;
        newErrors.ueberschrift1 = checkForbiddenWords(ueberschrift1) || newErrors.ueberschrift1;
        newErrors.ueberschrift2 = checkForbiddenWords(ueberschrift2) || newErrors.ueberschrift2;
        newErrors.articleText = checkForbiddenWords(articleText) || newErrors.articleText;
      }
      // previewDescription
      if (previewDescription.trim().length < 50 || previewDescription.trim().length > 250) {
        newErrors.previewDescription = "Der Vorschautext muss einen Text zwischen " + 50 + " und " + 250 + " Zeichen enthalten!";
      }
      // ueberschrift1
      if (ueberschrift1.trim().length < 10 || ueberschrift1.trim().length > 50) {
        newErrors.ueberschrift1 = "Die Überschrift 1 muss einen Text zwischen " + 10 + " und " + 50 + " Zeichen enthalten!";
      }
      // ueberschrift2
      if (ueberschrift2.trim().length < 10 || ueberschrift2.trim().length > 80) {
        newErrors.ueberschrift2 = "Die Überschrift 2 muss einen Text zwischen " + 10 + " und " + 80 + " Zeichen enthalten!";
      }
      // articleText
      if (articleText.trim().length < 300) {
        newErrors.articleText = "Der Artikeltext muss einen Text mit mindestens " + 300 + " Zeichen enthalten!";
      }

      // Listen
      if (lieferumfang.length == 0) {
        newErrors.lieferumfang = "Füge bitte mindestens ein Objekt zum Lieferumfang hinzu!";
      } else {
        const lieferumfangErrors = [];
        lieferumfang.forEach(({quantity, text}, index) => {
          if (quantity == null || quantity == 0) lieferumfangErrors.push("Zeile " + (index + 1) + " muss die Menge > 0 haben!");
          else if (!quantity || isNaN(quantity) || quantity <= 0) {
            lieferumfangErrors.push("Zeile " + (index + 1) + " muss eine Zahl > 0 haben!");
          }
          if (text == null || text.trim() == "") lieferumfangErrors.push("Zeile " + (index + 1) + " muss ein Lieferelement enthalten!")
        });
        if (lieferumfangErrors.length > 0) {
          newErrors.lieferumfang = lieferumfangErrors.join("\n");
        }
      }
      // Produktdaten
      if (produktdaten.length == 0) {
        newErrors.produktdaten = "Füge bitte mindestens eine Eigenschaft zu den Produktdaten hinzu!";
      } else {
        const produktdatenErrors = [];
        produktdaten.forEach((value, index) => {
          if (value == null || value.trim() == "") produktdatenErrors.push("Zeile " + (index + 1) + " muss einen Text enthalten!");
        })
        if (produktdatenErrors.length > 0) {
          newErrors.produktdaten = produktdatenErrors.join("\n");
        }
      }
      // Produktdetails
      if (JSON.stringify(produktdetails) === JSON.stringify(defaultProduktdetails)) {
        newErrors.produktdetails = "Füge mindestens eine Eigenschaft zu den Produktdetails hinzu!"
      } else {
        const isValidLabel = (item) => {
          if (!item.label.trim()) return false; // Stop if label is empty or null

          for (const child of item.children) {
            if (!isValidLabel(child)) return false; // Stop immediately if any child is invalid
          }

          return true;
        };

        if (!produktdetails.every(isValidLabel)) {
          newErrors.produktdetails = "Jedes Element in Produktdetails muss einen Text enthalten!";
        }
      }
    // Stelle sicher, dass nach Besondere Merkmale kein anderer Unterpunkt in der liste ist
    const besondereIndex = produktdetails.findIndex(
      (item) => item.label.trim().toLowerCase() === "besondere merkmale:"
    );

    if (besondereIndex !== -1) {
      const besondereItem = produktdetails[besondereIndex];

      // 1. Rule: must be the last outermost item
      if (besondereIndex < produktdetails.length - 1) {
        newErrors.produktdetails =
          '"Besondere Merkmale:" darf nicht gefolgt werden von weiteren Hauptelementen! Alle nachfolgenden Punkte müssen Unterpunkte davon sein. Hauptelemente sind die äußersten Listenelemente!';
      }

      // 2. Rule: must have at least one subitem
      else if (!besondereItem.children || besondereItem.children.length === 0) {
        newErrors.produktdetails =
          '"Besondere Merkmale:" muss mindestens ein Unterelement enthalten!';
      }
    }


      setProduktTexteErrors(newErrors);

      const firstErrorKey = Object.keys(newErrors).find((key) => newErrors[key]);
      if (firstErrorKey && errorRefs[firstErrorKey]?.current) {
        errorRefs[firstErrorKey].current.scrollIntoView({
          //behavior: "smooth",     // this led to the scrolling not working in chrome on my PC
          block: "center"
        });
      }
      if (Object.values(newErrors).some((error) => error)) return;


      setProduktTexteInfoMessage("");
      setSelectedTab(5);

      let produkttexte_error_string = "";
      if (!completedRequests.set_availability_masse_mainwarehouse_ek_price) {
        // Width, Lenght/Depth, Height in MM umwandeln
        const widthMM = Number(width) * 10;
        const lengthOrDepthMM = Number(lengthOrDepth) * 10;
        const heightMM = Number(height) * 10;
        // Weight in G umwandeln
        const weightG = weight ? Number(weight) * 1000 : null;
        console.log(widthMM, lengthOrDepthMM, heightMM, weightG);

        setProduktTexteInfoMessage("Lade Verfügbarkeit, Maße, Hauptlager, Einkaufspreis und Überschrift1 hoch");
        const dataAvailMasseMainwarehouseEkpriceName1And2 = {
          varID: varID,
          name1: name1And2,
          varNr: varNr,
          mainWarehouseId: isLagerarticle ? selectedMainWarehouseId.value : selectedManufacturerKind.value,
          availability: selectedAvailability,
          ekPrice: priceEK,
          widthMM: widthMM,
          lengthOrDepthMM: lengthOrDepthMM,
          heightMM: heightMM,
          weightG: weightG
        };
        await axios
          .put(`${backendUrl}/item_creator/set_availability_masse_mainwarehouse_ek_price/`, dataAvailMasseMainwarehouseEkpriceName1And2, config)
          .then(() => {
            setCompletedRequests(prev => ({...prev, set_availability_masse_mainwarehouse_ek_price: true}));
            console.log("Set Availability, Maße, mainwarehouse and EK price successful");
          })
          .catch((err) => {
            const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
              : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
                : err.response.data?.detail?.toString();
            setCompletedRequests(prev => ({...prev, set_availability_masse_mainwarehouse_ek_price: false}));
            alert(error_str);
            produkttexte_error_string += error_str + "\n";
          });
      }

      if (!completedRequests.set_texts) {
        setProduktTexteInfoMessage("Lade Texte hoch");
        const dataTexts = {
          itemID: itemID,
          varID: varID,
          name1And2: name1And2,
          name3: name3,
          previewDescription: previewDescription,
          description: generateHtmlPreview(),
          hasAlreadyBeenSet: alreadyProcessedPostRequests.set_texts
        };
        await axios
          .post(`${backendUrl}/item_creator/set_texts/`, dataTexts, config)
          .then(() => {
            setCompletedRequests(prev => ({...prev, set_texts: true}));
            setAlreadyProcessedPostRequests(prev => ({...prev, set_texts: true}));
            console.log("Set Texts successul");
          })
          .catch((err) => {
            const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
              : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
                : err.response.data?.detail?.toString();
            setCompletedRequests(prev => ({...prev, set_texts: false}));
            alert(error_str);
            produkttexte_error_string += error_str + "\n";
          });
      }

      setProduktTexteErrorMessage(produkttexte_error_string);
    }
  ;
  const RenderProdukttextePanel = () => {

    const info_name_1_2 = "Dies ist der Titel des Artikels, der im Webshop oder auf Nationalen Verkaufsportalen angezeigt wird. Bitte beschreibe hier den Artikel in wenigen Worten präzise. Inhalt des Namens sollte sein: Produkttyp, (ggf. ein weiterer passender Produkttyp) (ggf. ein tatsächlicher Name), Maß, Material, Farbe. Hierbei darf die maximale Zeichenanzahl von 80 Zeichen nicht überschritten werden.\r\n\n"
      + "Es soll jedoch immer die maximale Zeichenanzahl ausgereizt werden. Ggf. weitere nützliche Suchbegriffe angeben.\r\n\n"
      + "Beispiel: Esstisch Tula aus Wildeiche massiv 80x130x78 mit schwarzen Metallbeinen (71 Zeichen)";
    const info_name_3 = "Dies ist der Titel des Artikels, der auf der Onlineplattform OTTO angezeigt wird. Bitte beschreibe hier den Artikel in wenigen Worten präzise. Inhalt des Namens sollte sein: Produkttyp, (ggf. ein weiterer passender Produkttyp) (ggf. ein tatsächlicher Name), Maß, Material, Farbe. Hierbei darf die maximale Zeichenanzahl von 70 Zeichen nicht überschritten werden.\r\n\n"
      + "Es soll jedoch immer die maximale Zeichenanzahl ausgereizt werden. Ggf. weitere nützliche Suchbegriffe angeben.\r\n\n"
      + "Beispiel: Esstisch Tula Wildeiche massiv 80x130x78 mit schwarzen Metallbeinen (67 Zeichen)";
    const info_vorschautext = " In diesem Feld wird der Vorschautext eingefügt. Er muss immer \". Maße:\" beinhalten! Deine\n" +
      "                Maße aus dem vorherigen Tab werden automatisch eingefügt. Gehst du zurück und änderst sie, dann werden sie hier\n" +
      "                aktualisiert. Dein vorheriger Text bleibt bestehen. \nFalls du einen Artikel kopierst, wird automatisch der Vorschautext des kopierten Artikels eingefügt.\n" +
      "                Falls du diesen änderst und den ursprünglichen Text des kopierten Artikels wiederherstellen möchtest, dann\n" +
      "                entferne den kompletten Vorschautext, gehe in den Maße-Tab und drücke noch einmal auf den grünen Knopf!"
    const info_ueberschrift_1 = "Diese Überschrift leitet die Artikelbeschreibung ein und sollte kurz in 3-5 Worten den Artikel beschreiben.\r\n\n"
      + "Beispiel: Eine Gartenbank für drei Personen  oder  Die Teak-Bank für den Garen  oder  Eine hochwertige Bank aus massivem Teak";
    const info_ueberschrift_2 = "Diese Überschrift leitet den Artikeltext ein und sollte etwas länger, als die Überschrift 1 sein und sich von dieser unterscheiden. Darf auch 2-teilig sein:\r\n"
      + "Beispiel: Die Gartenbank aus Teak – Ein massives Highlight in jedem Garten  oder  Der Eye-Catcher für Balkon, Terrasse oder Garten – Gartenbank aus massivem Teak";
    return <div className="Tab">
      <Stack {...stackProps}>
        <CustomTextField label={"Name 1 und 2"}
                         ref={name1And2Ref}
                         value={name1And2}
                         onChange={(e) => {
                           setName1And2(e.target.value);
                           setCompletedRequests(prev => ({
                             ...prev,
                             set_availability_masse_mainwarehouse_ek_price: false,
                             set_texts: false,
                             set_images: false
                           }));
                         }}
                         inputMode={"text"}
                         is_required={true}
                         manualError={!!produktTexteErrors.name1And2}
                         errorText={produktTexteErrors.name1And2 || ""}
                         minLength={20} maxLength={80} endAdornment={name1And2.length + "/" + 80}
                         sx={{width: "80%"}}
                         onBlur={(e) => {
                           const firstWord = name1And2.trim().split(/[ \t\n,?;.:!]/, 2)[0];
                           setLieferumfangSuggestions([...defaultLieferumfangSuggestions.map(suggestion => firstWord + " " + suggestion),
                             ...defaultLieferumfangSuggestions]);
                           if (typeof e.target.onBlur === "function") e.target.onBlur(e);
                         }}

        />
        <HooverTooltip content={info_name_1_2}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"Name 3"}
                         ref={name3Ref}
                         value={name3}
                         onChange={(e) => {
                           setName3(e.target.value);
                           setCompletedRequests(prev => ({...prev, set_texts: false}));
                         }}
                         inputMode={"text"}
                         is_required={true}
                         manualError={!!produktTexteErrors.name3}
                         errorText={produktTexteErrors.name3 || ""}
                         minLength={20} maxLength={70} endAdornment={name3.length + "/" + 70}
                         sx={{width: "80%"}}/>
        <HooverTooltip content={info_name_3}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"Vorschautext"}
                         ref={previewDescriptionRef}
                         value={previewDescription}
                         onChange={(e) => {
                           setPreviewDescription(e.target.value.replace("\n", ''));
                           setCompletedRequests(prev => ({...prev, set_texts: false}));
                         }}
                         inputMode={"text"}
                         is_required={true}
                         addCopyButton={true}
                         addEraseButton={true}
                         eraseText={isLengthUsed
                           ? `. Maße: ca.: Länge x Breite x Höhe: ca. ${lengthOrDepth} x ${width} x ${height} cm`
                           : `. Maße: ca.: Breite x Höhe x Tiefe: ca. ${width} x ${height} x ${lengthOrDepth} cm`}
                         manualError={!!produktTexteErrors.previewDescription}
                         errorText={produktTexteErrors.previewDescription || ""}
                         is_multiline={true}
                         minLength={50} maxLength={250} endAdornment={previewDescription.length + "/" + 250}
                         sx={{width: "80%"}}/>
        <HooverTooltip content={info_vorschautext}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"Überschrift 1"}
                         ref={ueberschrift1Ref}
                         value={ueberschrift1}
                         onChange={(e) => {
                           setUeberschrift1(e.target.value);
                           setCompletedRequests(prev => ({...prev, set_texts: false}));
                         }}
                         inputMode={"text"}
                         is_required={true}
                         addCopyButton={true}
                         addEraseButton={true}
                         manualError={!!produktTexteErrors.ueberschrift1}
                         errorText={produktTexteErrors.ueberschrift1 || ""}
                         minLength={10} maxLength={50} endAdornment={ueberschrift1.length + "/" + 50}
                         sx={{width: "60%"}}/>
        <HooverTooltip content={info_ueberschrift_1}/>
      </Stack>
      <Stack {...stackProps}>
        <AutocompleteNumberTextInput items={lieferumfang} setItems={(items) => {
          setLieferumfang(items);
          setCompletedRequests(prev => ({...prev, set_texts: false}));
        }
        } suggestions={lieferumfangSuggestions}
                                     headline={"Lieferumfang hinzufügen"} numberfieldLabel={"Menge"}
                                     textfieldLabel={"Lieferelement"} ref={lieferumfangRef}/><EmptyTooltipPlaceholder/>
      </Stack>
      {
        renderErrorMessage(produktTexteErrors.lieferumfang)
      }
      <Stack {...stackProps}>
        <AutocompleteTextInput items={produktdaten} setItems={(items) => {
          setProduktdaten(items);
          setCompletedRequests(prev => ({...prev, set_texts: false}));
        }
        } suggestions={produktdatenSuggestions}
                               headline={"Produktdaten hinzufügen"} textfieldLabel={"Eigenschaft"}
                               ref={produktdatenRef}/>
        <EmptyTooltipPlaceholder/>
      </Stack>
      {
        renderErrorMessage(produktTexteErrors.produktdaten)
      }
      <Stack {...stackProps}>
        <AutocompleteTextInputTree items={produktdetails} setItems={(items) => {
          setProduktdetails(items);
          setCompletedRequests(prev => ({...prev, set_texts: false}));
        }
        } suggestions={produktdetailsSuggestions}
                                   headline={"Produktdetails hinzufügen"} textfieldLabel={"Detail"}
                                   ref={produktdetailsRef}/><EmptyTooltipPlaceholder/>
      </Stack>
      {
        renderErrorMessage(produktTexteErrors.produktdetails)
      }
      <Stack {...stackProps}>
        <CustomTextField label={"Überschrift 2"}
                         ref={ueberschrift2Ref}
                         value={ueberschrift2}
                         onChange={(e) => {
                           setUeberschrift2(e.target.value);
                           setCompletedRequests(prev => ({...prev, set_texts: false}));
                         }}
                         inputMode={"text"}
                         is_required={true}
                         addCopyButton={true}
                         addEraseButton={true}
                         manualError={!!produktTexteErrors.ueberschrift2}
                         errorText={produktTexteErrors.ueberschrift2 || ""}
                         minLength={10} maxLength={80} endAdornment={ueberschrift2.length + "/" + 80}
                         sx={{width: "80%"}}/>
        <HooverTooltip content={info_ueberschrift_2}/>
      </Stack>
      <Stack {...stackProps}>
        <CustomTextField label={"Artikeltext"}
                         ref={articleTextRef}
                         value={articleText}
                         onChange={(e) => {
                           setArticleText(e.target.value);
                           setCompletedRequests(prev => ({...prev, set_texts: false}));
                         }}
                         inputMode={"text"}
                         is_multiline={true}
                         is_required={true}
                         addCopyButton={true}
                         addEraseButton={true}
                         manualError={!!produktTexteErrors.articleText}
                         errorText={produktTexteErrors.articleText || ""}
                         minLength={300} endAdornment={articleText.length}
                         sx={{width: "80%"}}/>
        <EmptyTooltipPlaceholder/>
      </Stack>
      <div style={{display: "flex", flexDirection: "row", justifyContent: "center", gap: "15%", maxWidth: "30%"}}>
        {/* This was removed upon request <CustomCheckbox label={"Ignoriere Wortprüfung"} value={isIgnoreInvalidWords} onChange={checked => setIsIgnoreInvalidWords(checked)} single_line_label={true}/>*/}
        <DefaultButton label={"Öffne Rechtschreibprüfung"} onClick={() => {
          window.open('https://rechtschreibpruefung24.de/', '_blank', 'noopener,noreferrer');
        }}/>
        <DefaultButton label={"Erzeuge Vorschau"} onClick={() => setHtmlPreview(generateHtmlPreview())}/>
      </div>
      <Typography component="div" sx={{
        width: "80%",
        maxWidth: "80%",
        border: "1px solid white",
        borderRadius: "15px",
        borderColor: "white",
        textAlign: "left",
        paddingLeft: "15px",
        paddingRight: "15px"
      }}
                  dangerouslySetInnerHTML={{__html: htmlPreview}}/><Stack {...stackProps}>
      <SubmitButton label={"Speichern und weiter"} sx={{width: "50%", minWidth: "50%"}}
                    onClick={checkProdukttexte}/>
      <EmptyTooltipPlaceholder/>
    </Stack>
      {
        renderInfoMessage(produktTexteInfoMessage)
      }
      {
        renderErrorMessage(produktTexteErrorMessage)
      }
    </div>
      ;
  };

  const RenderProduktbilderPanel = () => {
    // when "Speichern und weiter" Button is pressed
    const checkImages = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      setErrorMessageImageSize("");
      setImagesInfoMessage("");

      if (images[0] === null) {
        setErrorMessageImageSize("Bitte lade mindestens ein Bild hoch")
      } else {
        setImagesInfoMessage("");
        setSelectedTab(6);      // go to next tab (Item Erstellen)

        const uploadedImages = images.filter(e => e !== null)

        if (!completedRequests.set_images) {
          setImagesInfoMessage("Lade Bilder hoch")
          // Create special alternate text
          const _deepestInitialCategoryID = getDeepestCategoryID()
          const _deepestInitialCatName = selectedCategories.get(_deepestInitialCategoryID);
          const _masse = isLengthUsed
            ? `${lengthOrDepth} x ${width} x ${height}` // Länge Breite Höhe
            : `${width} x ${height} x ${lengthOrDepth}`; // Breite Höhe Tiefe
          const selectedWoodTypesList = selectedWoodTypes.map((m) => m.label);
          const selectedMaterialsList = selectedMaterials.map((m) => m.label);
          const _material = selectedWoodTypesList.length && selectedMaterialsList.length
            ? `${selectedWoodTypesList.join(", ")}, ${selectedMaterialsList.join(", ")}`
            : selectedWoodTypesList.length
              ? `${selectedWoodTypesList.join(", ")}`
              : `${selectedMaterialsList.join(", ")}`;
          const _farbe = selectedColors ? "Farbe " + selectedColors.map((color) => color.label).join(", ") : "";
          let img1AlternateText = `Ein ${_deepestInitialCatName} im Maß ${_masse} aus ${_material}. ${_farbe}`;
          if (img1AlternateText.length > 125) {
            img1AlternateText = img1AlternateText.substring(0, 125);
          }

          // Upload images
          const dataImages = {
            itemID: itemID,
            name1And2: name1And2,
            img1AlternateText: img1AlternateText,
            images: uploadedImages.map(img => img.includes(',') ? img.split(',')[1] : img), // images are in "filename,data" format
            file_names: uploadedImages.map(img => img.includes(',') ? img.split(',')[0] : "No Name given"), // images are in "filename,data" format
            mandants: selectedShops.map(s => s.value),
            hasAlreadyBeenSet: alreadyProcessedPostRequests.set_images
          };
          // Check payload size to make sure it is under the limit
          const payloadStr = JSON.stringify(dataImages);
          const payloadBlob = new Blob([payloadStr], {type: 'application/json'});
          const payloadBytes = payloadBlob.size;
          const MAX_BYTES = 250 * 1024 * 1024; // = 262,144,000 bytes
          if (payloadBytes > MAX_BYTES) {
            // 1) Berechne Original-Bytes (deine Bilder als Base64-Strings liegen ja schon vor)
            const totalOriginalBytes = uploadedImages.reduce((sum, entry) => {
              const raw = entry.includes(',') ? entry.split(',')[1] : entry;
              const padding = raw.endsWith('==') ? 2 : raw.endsWith('=') ? 1 : 0;
              return sum + (raw.length * 3 / 4 - padding);
            }, 0);
            const origMB = (totalOriginalBytes / (1024 * 1024)).toFixed(1);
            const payloadMB = (payloadBytes / (1024 * 1024)).toFixed(1);
            const maxMB = 250;
            setErrorMessageImageSize(
              `Deine Bilder haben zusammen ${origMB} MB, ` +
              `durch Base64-Kodierung entstehen daraus ca. ${payloadMB} MB Upload-Daten. ` +
              `Erlaubt sind maximal ${maxMB} MB. ` +
              `Bitte komprimiere die größten Bilder, um unter das Limit zu kommen.`
            );
            setSelectedTab(5);
            return;  // stop before calling axios
          }

          await axios
            .post(`${backendUrl}/item_creator/set_images/`, dataImages, config)
            .then(() => {
              setCompletedRequests(prev => ({...prev, set_images: true}));
              setAlreadyProcessedPostRequests(prev => ({...prev, set_images: true}));
              console.log("Set Images successful");
              setImagesInfoMessage("Bilder erfolgreich hochgeladen");
            })
            .catch((err) => {
              const respData = err.response && err.response.data;
              const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
                : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
                  : err.response.data?.detail?.toString();

              // Update the completedRequests flag to false based on error response.
              setCompletedRequests(prev => ({...prev, set_images: respData?.completedRequests || false}));
              // Update the alreadyProcessedPostRequests flag to true as per error response.
              setAlreadyProcessedPostRequests(prev => ({
                ...prev,
                set_images: respData?.alreadyProcessedPostRequests || true
              }));

              alert(error_str);
              setErrorMessageImageSize(error_str);
            });
        }

      }
    }

    const handleImageUpload = (newImages: string[]) => {
      setCompletedRequests(prev => ({...prev, set_images: false}));
      let alertMaxImagesShown = false;
      let alertDuplicateImagesShown = false;
      setImages((prevImages) => {
        const updatedImages = [...prevImages];

        newImages.forEach((img) => {
          const emptyIndex = updatedImages.indexOf(null); // Find first empty slot

          // check for available empty slot and for image duplicate
          if (emptyIndex !== -1) {
            // Fill empty slot if image does not already exist
            if (!updatedImages.includes(img)) {
              updatedImages[emptyIndex] = img;
            } else if (!alertDuplicateImagesShown) {
              alert("Ein oder mehrere der ausgewählten Bilder sind bereits hochgeladen.")
              alertDuplicateImagesShown = true;
            }
          } else if (!alertMaxImagesShown) {
            alert('Maximale Anzahl an Bildern erreicht. Lösche ein Bild, um ein neues hochzuladen.')
            alertMaxImagesShown = true;
          }
        });

        return updatedImages;
      });
    };

    const handleImageDelete = (index: number) => {
      setCompletedRequests(prev => ({...prev, set_images: false}));
      setImages((prevImages) => {
        const updatedImages = [...prevImages];
        updatedImages.splice(index, 1); // Remove the selected image
        updatedImages.push(null); // Add a null at the end to maintain length
        return updatedImages;
      });
    };

    const onDragEnd = (event: DragEndEvent) => {
      const {active, over} = event;
      if (active.id !== over.id) {
        setCompletedRequests(prev => ({...prev, set_images: false}));
        setImages((prevImages) => {
          const oldIndex = Number(active.id);
          const newIndex = Number(over.id);

          const updatedImages: (string | null)[] = arrayMove(prevImages, oldIndex, newIndex);
          updatedImages.sort((a, b) => (a === null ? 1 : 0) - (b === null ? 1 : 0));

          return updatedImages;
        });
      }
    };


    const info_dnd = "Wähle Produktbilder via Drag and Drop oder durch eine Click aus. \n" +
      "Halte STRG und ziehe Bilder um ihre Position zu verändern."
    return (
      <div className="Tab">
        <Stack {...stackProps}>
          <Typography variant="body1" gutterBottom style={{width: "80%"}}>
            Vorsicht: Bilder lassen sich nur einmal korrigieren! Du kannst also Bilder hochladen und dann einmal
            zurückgehen, um neue Bilder hinzuzufügen (falls du welche vergessen hast), vorherig hochgeladene
            Bilder zu
            löschen oder die Reihenfolge zu ändern!
          </Typography>
          <HooverTooltip content={info_dnd}/>
        </Stack>
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={images.map((_, index) => index)}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "1px",
                width: "100%",
              }}
            >
              {images.map((image, index) => (
                <DragAndDrop
                  key={index}
                  index={index}
                  image={image}
                  onImageUpload={handleImageUpload}
                  //checkImageSize={checkImageSizeOnUpload}
                  onDelete={() => handleImageDelete(index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {errorMessageImageSize && renderErrorMessage(errorMessageImageSize)}
        <SubmitButton
          label={"Speichern und weiter"}
          sx={{width: "100%", minWidth: "50%"}}
          onClick={checkImages}
        />
        {renderInfoMessage(imagesInfoMessage)}
      </div>
    );
  };

  const RenderItemCreationPanel = () => {
    const getStateForListingCreator = () => {
      const stateDict = new Map<string, string | number | null>;

      stateDict.set("itemID", itemID);
      stateDict.set("varID", varID);
      stateDict.set("ebayID1", selectedEbayCat ? selectedEbayCat.ebayCatId1 : null);
      stateDict.set("ebayID2", selectedEbayCat ? selectedEbayCat.ebayCatId1 : null);
      stateDict.set("hoodID", selectedHoodCat ? selectedHoodCat.hoodCatId : null);
      // stateDict.set("Marke", selectedManufacturer.manufacturerName);
      stateDict.set("Hoehe", height);
      stateDict.set("Breite", width);
      stateDict.set("Laenge", lengthOrDepth);
      const selectedWoodTypesList = selectedWoodTypes.map((m) => m.label);
      const selectedMaterialsList = selectedMaterials.map((m) => m.label);
      // Combine into a comma-separated string
      const materialString = selectedWoodTypesList.length && selectedMaterialsList.length
        ? `${selectedWoodTypesList.join(", ")}, ${selectedMaterialsList.join(", ")}`
        : selectedWoodTypesList.length
          ? `${selectedWoodTypesList.join(", ")}`
          : `${selectedMaterialsList.join(", ")}`;
      stateDict.set("Material", materialString);
      stateDict.set("Liegeflaeche", selectedLiegeFlaeche ? selectedLiegeFlaeche.label : null);
      stateDict.set("Farbe", selectedColors ? selectedColors.map((color) => color.label).join(", ") : "");
      const sizeString = isLengthUsed
        ? `Maße: ca.: Länge x Breite x Höhe: ca. ${lengthOrDepth} x ${width} x ${height} cm`
        : `Maße: ca.: Breite x Höhe x Tiefe: ca. ${width} x ${height} x ${lengthOrDepth} cm`;
      stateDict.set("Groesse", sizeString);

      return Object.fromEntries(stateDict);
    };


    return (<div className="Tab">
      <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start"}}>

        {/* Stammdaten Tab */}

        <p style={{marginRight: "10px"}}>Stammdaten Tab:</p>
        <div style={{display: "flex", flexWrap: "wrap", gap: "10px"}}>
          <CustomCheckbox label="Stammdaten" value={completedRequests.put_stammdaten} onChange={() => {
          }} is_disabled={true}/>
          <CustomCheckbox label="Märkteverfügbarkeiten" value={completedRequests.set_availability_to_variation_markets}
                          onChange={() => {
                          }} is_disabled={true}/>
          <CustomCheckbox label="Lieferant" value={completedRequests.set_variationsupplier} onChange={() => {
          }} is_disabled={true}/>
          <CustomCheckbox label="Warenbestand" value={completedRequests.set_stock} onChange={() => {
          }} is_disabled={true}/>
          <CustomCheckbox label="EAN" value={completedRequests.post_ean1} onChange={() => {
          }} is_disabled={true}/>
          <CustomCheckbox label="EAN2" value={completedRequests.post_ean2} onChange={() => {
          }} is_disabled={true}/>
        </div>

        {/* Preisangaben Tab */}
        <div style={{display: "flex", alignItems: "center", marginBottom: "10px"}}>
          <p style={{marginRight: "10px"}}>Preisangaben Tab:</p>
          <CustomCheckbox label="Preise" value={completedRequests.set_prices} onChange={() => {
          }} is_disabled={true}/>
        </div>

        {/* Kategorien Tab */}
        <div style={{display: "flex", alignItems: "center", marginBottom: "10px"}}>
          <p style={{marginRight: "10px"}}>Kategorien Tab:</p>
          <CustomCheckbox label="Kategorien" value={completedRequests.set_categories} onChange={() => {
          }} is_disabled={true}/>
        </div>

        {/* Maße Tab */}
        <div style={{display: "flex", alignItems: "center", marginBottom: "10px"}}>
          <p style={{marginRight: "10px"}}>Maße Tab:</p>
          <CustomCheckbox label="Eigenschaften" value={completedRequests.set_variation_properties}
                          onChange={() => {
                          }} is_disabled={true}/>
        </div>

        {/* Produkttexte Tab */}
        <div style={{display: "flex", alignItems: "center", marginBottom: "10px"}}>
          <p style={{marginRight: "10px"}}>Produkttexte Tab:</p>
          <div style={{display: "flex", flexWrap: "wrap", gap: "10px"}}>
            <CustomCheckbox
              label="Verfügbarkeit, Maße, Hauptlager, EK-Preis, Variantenname, Ext. Varianten-ID"
              value={completedRequests.set_availability_masse_mainwarehouse_ek_price} onChange={() => {
            }} is_disabled={true}/>
            <CustomCheckbox label="Texte" value={completedRequests.set_texts} onChange={() => {
            }} is_disabled={true}/>
          </div>
        </div>

        {/* Produktbilder Tab */}
        <div style={{display: "flex", alignItems: "center", marginBottom: "10px"}}>
          <p style={{marginRight: "10px"}}>Produktbilder Tab:</p>
          <CustomCheckbox label="Bilder" value={completedRequests.set_images} onChange={() => {
          }} is_disabled={true}/>
        </div>

      </div>
      {Object.values(completedRequests).every(val => val === true) && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          width: "100%",
          justifyContent: "center",
          marginTop: 0,
          paddingTop: 0
        }}>
          <p style={{color: " var(--naturGreen)", margin: "0 0 15px 0"}}>Der Artikel wurde erfolgreich erstellt! </p>
          <div style={{display: "flex", flexDirection: "row", gap: "50px", width: "100%", justifyContent: "center"}}>
            <SubmitButton label={"Listing erstellen"}
                          onClick={() => {
                            setCurrentApp("ListingCreator");
                            startTransition(() => {
                              navigate("/ListingCreator", {state: getStateForListingCreator()})
                            });
                          }}
                          sx={{width: "40%", minWidth: "40%"}}/>
            <DefaultButton label={"Neuen Artikel erstellen"} onClick={() => window.location.reload()}
                           sx={{width: "20%", minWidth: "20%"}}/>
          </div>
        </div>
      )}
    </div>);
  };


  return (
    <>
      <Navbar showWarningWhenClicked={true}/>
      <Helmet>
        <title>{"Item Erstellen"}</title>
      </Helmet>
      <h1 style={{marginTop: "50px"}}>Artikel erstellen</h1>
      {!isItemCreatorVisible && (<>{RenderInitialItemCreatorPage()}</>)}
      {renderInfoMessage(initialCreationInfoMessage)}
      {isItemCreatorVisible && renderErrorMessage(initialCreationErrorMessage)}
      {isItemCreatorVisible && (
        <div className="ItemCreatorPage">
          <Tabs
            value={selectedTab}
            onChange={(_, newVal) => {
              setSelectedTab(newVal);
            }}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="scrollable force tabs example"
            TabIndicatorProps={{style: {backgroundColor: 'var(--naturGreen)'}}}
          >
            {["Stammdaten", "Preisangaben", "Kategorien", "Maße", "Produkttexte", "Produktbilder", "Item erstellen!"].map((label, index) => {
              let isDisabled = lastIncompleteTab !== -1 && index > lastIncompleteTab;
              if (index === 2 && completedRequests.set_categories === true) {
                isDisabled = true;
              }
              return (<Tab key={index} label={label} id={`tab-${index}`} aria-controls={`tab-${index}`}
                   sx={tabStyle}
                   disabled={isDisabled}/>);
            })}
          </Tabs>
          <CustomTabPanel value={selectedTab} index={0}>
            {RenderStammdatenPanel()}
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={1}>
            {RenderPreisangabenPanel()}
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={2}>
            {RenderCategoryPanel()}
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={3}>
            {RenderMetricsPanel()}
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={4}>
            {RenderProdukttextePanel()}
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={5}>
            {RenderProduktbilderPanel()}
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={6}>
            {RenderItemCreationPanel()}
          </CustomTabPanel>
        </div>
      )}
      {renderErrorMessage(errorMessage)}
      {renderInfoMessage(infoMessage)}
      {ColorModeIcon()}
    </>
  );
}

export default ItemCreator;